// viewer.js — shared synced video+map viewer module.
// Vanilla ESM. No build step. Two callers: HDD index.html (local paths)
// and the future cloud React shell (signed R2 URLs).
//
// Spec: docs/superpowers/specs/2026-05-01-video-map-cloud-shared-viewer-design.md

import { parseCue } from "./viewer.parser.js";
import {
  pickNearestOnPath,
  macroSegmentEndpoints,
  medianDeltaSeconds,
  smoothPositionsInPlace,
} from "./viewer.geometry.js";
import {
  safeColor,
  tooltipText as buildTooltipText,
  cardHTML as buildCardHTML,
  noteRowHTML as buildNoteRowHTML,
  notesListHTML as buildNotesListHTML,
  migrateLegacyNote,
  appendNote,
  editNoteById,
  removeNoteById,
  canDeleteOthersNotes,
  isOwn as popupsIsOwn,
  isLegacyOrphan,
  shouldCommitName,
} from "./viewer.popups.js";
import {
  autoCapIndex, autoLevelBadge, isMaxLevel, isWorkerUrl, levelLabel, topLevelIndex,
} from "./viewer.quality.js";
import { createClickIntent } from "./viewer.intent.js";
import { createSeekTimingProbe } from "./viewer.debug.js";
import { mountDivider } from "./viewer.divider.js";
import { loadPlayhead, createPlayheadSaver } from "./viewer.playhead.js";
import { createZoomController } from "./viewer.zoom.js";
import { mountControls } from "./viewer.controls.js";
import { createPanoHost } from "./viewer.pano.js";
import { panoLabel, panoOrdinals } from "./viewer.labels.js";
import { computeSourceRect } from "./viewer.freeze.geometry.js";
import {
  buildPanoFrameMetadata,
  resolveFramePosition,
  buildCaptionLines,
} from "./viewer.freeze.metadata.js";
import {
  EXIF_SOFTWARE, buildThumbCanvas, canvasToJpegBlob, drawCaptionLines,
  encodeTaggedJpeg, renderFreezeFrame,
} from "./viewer.freeze.capture.js";
import { CaptureFatalError, createVideoGrabber, lazyGrabber } from "./viewer.grabber.js";
import { createFootprintController, mountFootprintToggle } from "./viewer.footprint.js";
import { mountFollowControl } from "./viewer.follow.js";
import { createOverlayRegistry } from "./viewer.overlay.js";
import { buildGhostLayers } from "./viewer.ghosts.js";

// Soft temporal bias for heli-marker drag scrubbing. Each second of
// |dt| between the previous-tick `t` and a candidate segment's t adds
// this many "equivalent pixels" of spatial distance to its score, so
// crossovers/figure-8 loops lock to the branch the user is already on.
// Soft (not a hard window) so a long deliberate drag can still travel
// between distant branches.
const DRAG_BIAS_PX_PER_SEC = 2;

const M_TO_FT = 3.28084; // display-only; stored telemetry stays metric

// 1x1 transparent PNG. Drawn in place of the satellite labels overlay when the
// host degrades the basemaps (the NSW services publish no labels layer), so the
// layer stays on the map — and in the layer control — without asking a paid
// provider for another tile.
const TRANSPARENT_TILE =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

// Default tile URL templates — the NSW Spatial Services (SIX Maps) public
// services. Chosen as the *default* (2026-09-09) because they need no API key
// and are Creative Commons Attribution, so the HDD bundle's viewer and the
// web app's offline cache can use them without a licence problem. The web
// app passes its own keyed Esri layers via `mountViewer({ tileLayers })`
// when it is online — see web/src/map/basemaps.ts. Both services are
// ArcGIS MapServer caches: {z}/{y}/{x} (row before column), 256 px.
// `resolveTileTemplates` (exported below) returns these unless an offline
// override is provided via `mountViewer({ tileTemplates: { map, satellite } })`.
const NSW_SIX = "https://maps.six.nsw.gov.au/arcgis/rest/services/public/";
const DEFAULT_TILE_TEMPLATES = {
  map: `${NSW_SIX}NSW_Base_Map/MapServer/tile/{z}/{y}/{x}`,
  satellite: `${NSW_SIX}NSW_Imagery/MapServer/tile/{z}/{y}/{x}`,
};
const NSW_ATTRIBUTION = "© State of New South Wales (Spatial Services)";

/**
 * Resolve basemap tile URL templates, honouring an optional offline override.
 *
 * @param {{ map?: string; satellite?: string } | undefined} override
 * @returns {{ map: string; satellite: string }}
 */
export function resolveTileTemplates(override) {
  return {
    map: override?.map ?? DEFAULT_TILE_TEMPLATES.map,
    satellite: override?.satellite ?? DEFAULT_TILE_TEMPLATES.satellite,
  };
}

/**
 * Tile layer config shape (also the `mountViewer({ tileLayers })` override):
 *
 *   street:    { url, attribution, subdomains?, minNativeZoom?, maxNativeZoom,
 *                maxZoom, tileSize?, zoomOffset? }
 *   satellite: { ...same..., labels?: { url, subdomains?, minNativeZoom?,
 *                maxNativeZoom, maxZoom, tileSize?, zoomOffset? } }
 *
 * `tileSize` / `zoomOffset` exist for 512 px providers (Esri's static
 * basemap tiles): Leaflet then requests level z-1 for map zoom z, and
 * `minNativeZoom: 1` keeps map zoom 0 from asking for level -1.
 * `labels` is an optional place-name overlay drawn over the satellite layer.
 */
export const DEFAULT_TILE_LAYERS = {
  street: {
    url: DEFAULT_TILE_TEMPLATES.map,
    attribution: NSW_ATTRIBUTION,
    // Both NSW services 404 above level 18 outside towns (measured
    // 2026-09-09 on the New England corridor; Armidale reaches 19–20).
    // Capping native zoom upscales instead of leaving holes; maxZoom stays
    // at 22 so users can still zoom in.
    maxNativeZoom: 18,
    maxZoom: 22,
  },
  satellite: {
    url: DEFAULT_TILE_TEMPLATES.satellite,
    attribution: NSW_ATTRIBUTION,
    maxNativeZoom: 18,
    maxZoom: 22,
  },
};

/**
 * Leaflet `L.tileLayer` options for one layer config — only the keys the
 * config sets, so a 256 px provider never gets a stray `tileSize`.
 *
 * @param {{ attribution?: string, subdomains?: string, minNativeZoom?: number,
 *   maxNativeZoom?: number, maxZoom?: number, tileSize?: number,
 *   zoomOffset?: number }} layer
 * @returns {Record<string, unknown>}
 */
export function leafletTileOptions(layer) {
  const out = {};
  for (const k of [
    "attribution", "subdomains", "minNativeZoom", "maxNativeZoom", "maxZoom",
    "tileSize", "zoomOffset",
  ]) {
    if (layer[k] !== undefined) out[k] = layer[k];
  }
  return out;
}

async function extractTelemetry(videoSrc) {
  if (!globalThis.MP4Box) {
    throw new Error("MP4Box global not found — load mp4box.min.js before viewer.js");
  }
  const file = globalThis.MP4Box.createFile();
  const cues = [];

  return new Promise((resolve, reject) => {
    let textTrackId = null;

    file.onError = (e) => reject(new Error("mp4box parse error: " + e));

    file.onReady = (info) => {
      const textTrack = info.tracks.find(
        (t) => t.codec === "tx3g" || t.codec === "mov_text" || t.type === "subtitles" || t.type === "text",
      );
      if (!textTrack) {
        reject(new Error("MP4 has no telemetry track. Use `video-map build` to produce video.fmv.mp4."));
        return;
      }
      textTrackId = textTrack.id;
      file.setExtractionOptions(textTrackId, null, { nbSamples: 100 });
      file.start();
    };

    file.onSamples = (id, _user, samples) => {
      if (id !== textTrackId) return;
      const decoder = new TextDecoder("utf-8");
      for (const s of samples) {
        // mov_text samples are length-prefixed — skip the first 2 bytes.
        const bytes = s.data.slice(2);
        const text = decoder.decode(bytes);
        const parsed = parseCue(text);
        if (parsed) {
          parsed.t = s.cts / s.timescale;  // seconds into the video
          cues.push(parsed);
        }
      }
    };

    fetchStreaming(videoSrc, file).then(() => {
      file.flush();
      cues.sort((a, b) => a.t - b.t);
      if (cues.length < 2) {
        reject(new Error(`extracted only ${cues.length} cue(s); expected ≥ 2`));
      } else {
        resolve(cues);
      }
    }).catch(reject);
  });
}

// Cloud-only: load telemetry from a small JSON sidecar produced by the
// generator. ~1000× less data on the wire than streaming the entire FMV
// to extract the muxed mov_text track via mp4box-js. The HDD bundle does
// not ship track.json — viewer falls back to extractTelemetry() above.
async function loadTrackJson(url) {
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`track.json fetch failed: ${resp.status} ${resp.statusText}`);
  }
  const payload = await resp.json();
  if (!payload || !Array.isArray(payload.samples)) {
    throw new Error("track.json: missing or invalid `samples` array");
  }
  if (payload.samples.length < 2) {
    throw new Error(
      `track.json: only ${payload.samples.length} sample(s); expected ≥ 2`,
    );
  }
  return payload.samples;
}

// Stream the FMV in a single HTTP connection rather than per-chunk Range
// requests. Previously this issued ~500 sequential 1 MB range fetches —
// each a full Worker→R2 redirect round-trip — which made a 500 MB FMV take
// minutes to parse. With streaming, the browser progressively delivers
// body chunks as they arrive over one TLS connection. mp4box-js handles
// the variable chunk sizes internally.
//
// Falls back to a chunked-Range loop with 8 MB chunks if the response has
// no readable body (rare; old polyfills, some service workers).
async function fetchStreaming(url, mp4boxFile) {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`fetch ${url} failed: ${resp.status}`);
  if (!resp.body || typeof resp.body.getReader !== "function") {
    return fetchInChunks(url, mp4boxFile);
  }
  const reader = resp.body.getReader();
  let offset = 0;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    const ab = value.buffer.slice(
      value.byteOffset, value.byteOffset + value.byteLength,
    );
    ab.fileStart = offset;
    mp4boxFile.appendBuffer(ab);
    offset += ab.byteLength;
  }
}

async function fetchInChunks(url, mp4boxFile) {
  const CHUNK = 8 * 1024 * 1024; // 8 MB (was 1 MB — too chatty)
  let offset = 0;
  while (true) {
    const resp = await fetch(url, {
      headers: { Range: `bytes=${offset}-${offset + CHUNK - 1}` },
    });
    if (!resp.ok && resp.status !== 206 && resp.status !== 200) {
      if (resp.status === 416) break;
      throw new Error(`fetch ${url} failed: ${resp.status}`);
    }
    const buf = await resp.arrayBuffer();
    if (buf.byteLength === 0) break;
    buf.fileStart = offset;
    mp4boxFile.appendBuffer(buf);
    offset += buf.byteLength;
    if (resp.status === 200) break;
    const cr = resp.headers.get("Content-Range");
    if (cr) {
      const total = parseInt(cr.split("/").pop(), 10);
      if (!isNaN(total) && offset >= total) break;
    }
    if (buf.byteLength < CHUNK) break;
  }
}

/**
 * Build the map and its basemap layers.
 *
 * @param {HTMLElement} mapEl
 * @param {Array<{lat: number, lon: number}>} telemetry
 * @param {object} tileLayers Resolved tile-layer set (see DEFAULT_TILE_LAYERS).
 * @param {Array<() => void>} cleanup
 * @param {(() => void)=} onTileLoad Called once per basemap tile request, so a
 *   host can meter a paid provider (web/src/map/tileMeter.ts).
 * @returns {object} `{ map, L, street, satellite, satLabels, trackLine }`
 */
export function mountMap(mapEl, telemetry, tileLayers, cleanup, onTileLoad) {
  if (!globalThis.L) {
    throw new Error("Leaflet global not found — load leaflet.js before viewer.js");
  }
  const L = globalThis.L;
  const map = L.map(mapEl, {
    zoomSnap: 0.5, wheelDebounceTime: 80, wheelPxPerZoomLevel: 120,
    // Draw vector layers (track, footprint, overlay) 50% beyond the viewport
    // so pans don't clip them at the edge until moveend re-renders.
    renderer: L.svg({ padding: 0.5 }),
    // leaflet-rotate (no-ops on a plain Leaflet build): rotation is driven
    // exclusively by the Follow control's heading-up mode — suppress the
    // plugin's own control and its shift+wheel gesture (on by default).
    rotate: true, rotateControl: false, shiftKeyRotate: false,
  });
  cleanup.push(() => map.remove());

  // Layers are constructed here but not added to the map — the IIFE in
  // mountViewer decides which one is the initial basemap (satellite by
  // default since 2026-05-05).
  const street = L.tileLayer(tileLayers.street.url, leafletTileOptions(tileLayers.street));
  const satellite = L.tileLayer(
    tileLayers.satellite.url, leafletTileOptions(tileLayers.satellite),
  );
  const labels = tileLayers.satellite.labels;
  const satLabels = labels
    ? L.tileLayer(labels.url, { ...leafletTileOptions(labels), pane: "overlayPane" })
    : null;

  // One call per tile the browser starts fetching — a billable request when the
  // host passed a keyed provider's layers.
  if (typeof onTileLoad === "function") {
    for (const layer of [street, satellite, satLabels]) {
      if (layer) layer.on("tileloadstart", onTileLoad);
    }
  }

  const coords = telemetry.map((p) => [p.lat, p.lon]);
  const trackLine = L.polyline(coords, { color: "#ff3333", weight: 3, opacity: 0.85 }).addTo(map);

  function dot(color, size) {
    return L.divIcon({
      html: `<div style="width:${size}px;height:${size}px;background:${color};border:2px solid #fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,.6)"></div>`,
      iconSize: [size, size], iconAnchor: [size / 2, size / 2], className: "",
    });
  }
  if (coords.length) {
    L.marker(coords[0], { icon: dot("#00ff88", 10) }).bindTooltip("Track start").addTo(map);
    L.marker(coords[coords.length - 1], { icon: dot("#ff4455", 10) }).bindTooltip("Track end").addTo(map);
  }

  if (coords.length > 1) {
    map.fitBounds(L.latLngBounds(coords), { padding: [20, 20] });
  } else if (coords.length === 1) {
    map.setView(coords[0], 14);
  }

  // Scale bar (metric + imperial lines), Google-Maps style. Removed
  // implicitly by map.remove() in cleanup.
  L.control.scale({ metric: true, imperial: true }).addTo(map);

  return { map, L, street, satellite, satLabels, trackLine };
}

// Layer options that differ between providers and must travel with a degrade:
// a layer left on Esri's maxNativeZoom 22 would ask NSW (native to 18) for
// tiles it does not have and draw blanks, and a 512 px layer would keep asking
// for level z-1 of a 256 px service.
const TILE_GEOMETRY_OPTIONS = ["minNativeZoom", "maxNativeZoom", "tileSize", "zoomOffset"];

/**
 * Swap every basemap for the given set without remounting (spec §8.2). Each
 * layer takes the incoming provider's tile geometry, credit and URL; labels
 * have no NSW equivalent and go transparent. `viewreset` makes every GridLayer
 * re-run _resetView so the new native-zoom ceiling is applied immediately.
 *
 * @param {object|null} mapHandles What mountMap returned, or null before it ran.
 * @param {object} set Tile-layer set to switch to.
 */
export function degradeTileLayers(mapHandles, set) {
  if (!mapHandles) return;
  const attributionControl = mapHandles.map.attributionControl;
  const swap = (layer, cfg) => {
    if (!layer || !cfg) return;
    for (const key of TILE_GEOMETRY_OPTIONS) {
      if (cfg[key] === undefined) delete layer.options[key];
      else layer.options[key] = cfg[key];
    }
    // The map credits whoever is actually serving the tiles (NSW's CC BY).
    if (attributionControl && typeof attributionControl.addAttribution === "function") {
      attributionControl.removeAttribution(layer.options.attribution);
      if (cfg.attribution) attributionControl.addAttribution(cfg.attribution);
    }
    layer.options.attribution = cfg.attribution;
    layer.setUrl(cfg.url);
  };
  swap(mapHandles.street, set.street);
  swap(mapHandles.satellite, set.satellite);
  if (mapHandles.satLabels) mapHandles.satLabels.setUrl(TRANSPARENT_TILE);
  if (typeof mapHandles.map.fire === "function") mapHandles.map.fire("viewreset");
}

/**
 * Degrade now, or stash the set until the map exists (mirrors
 * `_pendingOverlays`). `handle.degradeTiles` calls it with a set — possibly
 * before the async setup built the map — and the setup IIFE calls it once with
 * `null` to drain whatever was stashed, before the basemap goes on the map.
 *
 * @param {object} handle The viewer handle (holds `_pendingDegrade`).
 * @param {object|null} mapHandles What mountMap returned, or null.
 * @param {object|null} set Tile-layer set, or null to drain a stashed one.
 */
export function applyOrStashDegrade(handle, mapHandles, set) {
  const target = set ?? handle._pendingDegrade;
  if (!target) return;
  if (!mapHandles) {
    handle._pendingDegrade = target;
    return;
  }
  degradeTileLayers(mapHandles, target);
  handle._pendingDegrade = null;
}

// Slippy-tile coordinates for a given lat/lon at zoom z. Used to build the
// representative thumbnail rendered inside the basemap-swap swatch.
function lonLatToTile(lon, lat, z) {
  const n = 2 ** z;
  const x = Math.floor(((lon + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n,
  );
  return { x, y, z };
}

// Build a single-tile preview URL from a Leaflet tile-layer URL template.
// `zoomOffset` mirrors Leaflet's option: a 512 px layer at map zoom z
// serves tile level z-1, so the preview asks for that level too.
export function tilePreviewUrl(template, lat, lon, z, { zoomOffset = 0 } = {}) {
  const { x, y, z: zoom } = lonLatToTile(lon, lat, z + zoomOffset);
  return template
    .replace("{s}", "a")
    .replace("{z}", String(zoom))
    .replace("{x}", String(x))
    .replace("{y}", String(y));
}

// Helicopter SVG — top-down silhouette, nose pointing up (north) at 0°
// rotation. Rotates via outer wrapper transform; heading is derived from
// the polyline tangent so it works for both VFX (with gb_yaw) and GPX
// (no yaw) flights.
const HELI_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="44" height="44" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.6));cursor:grab">
  <g fill="#fff" stroke="#fff" stroke-linejoin="round">
    <path d="M16,7.2 C18.4,7.7 19.4,10.4 19.3,13.4 C19.2,16.2 18.3,18.4 17.1,19.6 L16.75,19.9 L15.25,19.9 L14.9,19.6 C13.7,18.4 12.8,16.2 12.7,13.4 C12.6,10.4 13.6,7.7 16,7.2 Z" stroke-width="1.3"/>
    <path d="M15.35,19.85 L16.65,19.85 L16.3,27.4 L15.7,27.4 Z" stroke-width="1.2"/>
    <rect x="12.9" y="24.3" width="6.2" height="0.95" rx="0.45" stroke-width="1.15"/>
    <rect x="15.1" y="28.1" width="1.8" height="0.85" rx="0.4" stroke-width="1.1"/>
  </g>
  <g fill="#1c1f24">
    <path d="M16,7.2 C18.4,7.7 19.4,10.4 19.3,13.4 C19.2,16.2 18.3,18.4 17.1,19.6 L16.75,19.9 L15.25,19.9 L14.9,19.6 C13.7,18.4 12.8,16.2 12.7,13.4 C12.6,10.4 13.6,7.7 16,7.2 Z"/>
    <path d="M15.35,19.85 L16.65,19.85 L16.3,27.4 L15.7,27.4 Z"/>
    <rect x="12.9" y="24.3" width="6.2" height="0.95" rx="0.45"/>
    <rect x="15.1" y="28.1" width="1.8" height="0.85" rx="0.4"/>
  </g>
  <g class="vmc-heli-rotor" style="transform-origin:16px 13px;transform-box:view-box">
    <g stroke="#fff" stroke-width="1.7" stroke-linecap="round">
      <line x1="16" y1="13" x2="24.56" y2="4.44"/><line x1="16" y1="13" x2="24.56" y2="21.56"/>
      <line x1="16" y1="13" x2="7.44" y2="21.56"/><line x1="16" y1="13" x2="7.44" y2="4.44"/>
    </g>
    <g stroke="#1c1f24" stroke-width="0.95" stroke-linecap="round">
      <line x1="16" y1="13" x2="24.41" y2="4.59"/><line x1="16" y1="13" x2="24.41" y2="21.41"/>
      <line x1="16" y1="13" x2="7.59" y2="21.41"/><line x1="16" y1="13" x2="7.59" y2="4.59"/>
    </g>
  </g>
  <circle cx="16" cy="13" r="1.15" fill="#1c1f24"/>
</svg>`;

function makeHeliIcon(L, heading) {
  const wrapped = `<div class="vmc-heli-wrap" style="transform:rotate(${heading || 0}deg);transform-origin:center">${HELI_SVG}</div>`;
  return L.divIcon({
    html: wrapped, iconSize: [44, 44], iconAnchor: [22, 22], className: "vmc-heli-icon",
  });
}

// Bearing from a to b in degrees CW from north. Equirectangular
// approximation — fine for the short segments between telemetry samples.
function bearing(a, b) {
  const RAD = Math.PI / 180;
  const dx = (b.lon - a.lon) * Math.cos(((a.lat + b.lat) / 2) * RAD);
  const dy = b.lat - a.lat;
  if (dx === 0 && dy === 0) return 0;
  return Math.atan2(dx, dy) * 180 / Math.PI;
}

function interpolate(telemetry, t, headingStride = 1) {
  if (!telemetry.length) return null;
  const n = telemetry.length;
  // Compute heading from two samples spanning `headingStride` apart so
  // the bearing is taken over a ~1 s / tens-of-metres baseline regardless
  // of cadence. At 24 Hz with stride=1 the baseline is ~1 m and GPS noise
  // dominates the bearing — that's the source of the heli jitter the
  // un-downsampled pipeline introduced. See viewer.geometry.js for the
  // stride helper.
  if (t <= telemetry[0].t) {
    const { aIdx, bIdx } = macroSegmentEndpoints(0, headingStride, n);
    const h = bIdx > aIdx ? bearing(telemetry[aIdx], telemetry[bIdx]) : 0;
    return { ...telemetry[0], heading: h, i: 0 };
  }
  if (t >= telemetry[n - 1].t) {
    const { aIdx, bIdx } = macroSegmentEndpoints(n - 1, headingStride, n);
    const h = bIdx > aIdx ? bearing(telemetry[aIdx], telemetry[bIdx]) : 0;
    return { ...telemetry[n - 1], heading: h, i: n - 1 };
  }
  let lo = 0, hi = n - 1;
  while (hi - lo > 1) {
    const m = (lo + hi) >> 1;
    if (telemetry[m].t <= t) lo = m; else hi = m;
  }
  const a = telemetry[lo], b = telemetry[hi];
  const f = b.t === a.t ? 0 : (t - a.t) / (b.t - a.t);
  const { aIdx, bIdx } = macroSegmentEndpoints(lo, headingStride, n);
  const h = bIdx > aIdx ? bearing(telemetry[aIdx], telemetry[bIdx]) : 0;
  return {
    lat: a.lat + (b.lat - a.lat) * f,
    lon: a.lon + (b.lon - a.lon) * f,
    abs_alt: a.abs_alt != null ? a.abs_alt + ((b.abs_alt ?? a.abs_alt) - a.abs_alt) * f : undefined,
    yaw: a.yaw,
    heading: h,
    i: lo,
  };
}

function fmtVid(s)  { const m = Math.floor(s / 60), ss = (s % 60).toFixed(1); return m + ":" + (ss < 10 ? "0" : "") + ss; }
function fmtClock(epochS, tz) {
  const d = new Date(epochS * 1000);
  const fmt = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: tz,
  });
  return fmt.format(d);
}
function fmtUTC(epochS) {
  return new Date(epochS * 1000).toISOString().slice(11, 19) + " UTC";
}

/**
 * Keep every text track on a media element disabled.
 *
 * The muxed FMV carries the telemetry as a mov_text/tx3g subtitle track.
 * Safari renders it by default — Chrome does not — dumping the raw SRT
 * payload over the picture, and our own control bar sits on top of the
 * native controls that would otherwise turn it off. The viewer never reads
 * the rendered cues (cloud reads track.json, HDD parses the mp4 with
 * mp4box), so the track is pure noise here.
 *
 * Tracks appear asynchronously while the container is parsed and Safari
 * re-asserts its default pick on load, so re-apply on every relevant event
 * rather than once at setup.
 *
 * @param {HTMLMediaElement|null} media Element to police. Safe on null.
 */
export function suppressTextTracks(media) {
  if (!media) return;
  const disableAll = () => {
    const tracks = media.textTracks;
    if (!tracks) return;
    for (let i = 0; i < tracks.length; i++) {
      // Guard the write: assigning `mode` fires "change", and an
      // unconditional set would re-enter this handler forever.
      if (tracks[i] && tracks[i].mode !== "disabled") {
        tracks[i].mode = "disabled";
      }
    }
  };
  disableAll();
  const tracks = media.textTracks;
  if (tracks && typeof tracks.addEventListener === "function") {
    tracks.addEventListener("addtrack", disableAll);
    tracks.addEventListener("change", disableAll);
  }
  if (typeof media.addEventListener === "function") {
    media.addEventListener("loadedmetadata", disableAll);
    media.addEventListener("loadeddata", disableAll);
  }
}

/** Whether a mousedown may begin a zoom-pan gesture on the video.
 *
 * The pan listener sits on .vmc-video-clip, and viewer.controls.js appends
 * the control bar *inside* that same element — so a mousedown on the scrub
 * bar, the play button or the speed menu bubbles to the pan listener too.
 * Without this guard, dragging the scrub bar while zoomed drags the picture
 * with it, and the button's own click gets swallowed on release.
 */
export function isPanGestureTarget(target) {
  if (!target || typeof target.closest !== "function") return false;
  return target.closest(".vmc-controls") === null;
}

/** The viewer's chrome, as a template string. Exported so the anchors the
 *  cloud tour spotlights (`data-tour`, spec 2026-09-04 §4.10) can be tested
 *  without mounting a video, a Leaflet map or a network — the markup lives
 *  here rather than at the call site so the test covers what ships.
 *  `framesBtn` is true when the host supplied an onOpenFrames handler (the
 *  cloud shell) and false for the HDD shell; `framesCount` seeds the live
 *  count in its label. */
export function buildChromeHTML({ framesBtn, framesCount }) {
  const frames = framesBtn
    ? '<button id="vmc-frames-btn" class="vmc-tbtn" type="button" '
      + 'data-tour="viewer.frames" disabled>📷 Saved Frames '
      + `(<span id="vmc-frames-count">${framesCount}</span>)</button>`
    : "";
  return `
    <div class="vmc-header"><span class="vmc-flight-name">loading…</span></div>
    <div class="vmc-main">
      <div class="vmc-video-panel">
        <div class="vmc-video-clip" data-tour="viewer.video">
          <video class="vmc-video" preload="metadata"></video>
          <video class="vmc-preview" muted preload="auto" tabindex="-1"></video>
        </div>
        <div class="vmc-info-bar" data-tour="viewer.infobar"></div>
        <div class="vmc-toolbar">
          <select class="vmc-tbtn vmc-quality" title="Playback quality" data-tour="viewer.quality" hidden>
            <option value="-1">Auto</option>
          </select>
          <span class="vmc-quality-now" title="What Auto is playing now" hidden></span>
          <span class="vmc-sync-group">
            <span class="vmc-lbl">Sync </span>
            <button class="vmc-tbtn vmc-sync-down" title="GPS −0.5s">−</button>
            <span class="vmc-val vmc-offset-val">0.0s</span>
            <button class="vmc-tbtn vmc-sync-up" title="GPS +0.5s">+</button>
            <button class="vmc-tbtn vmc-sync-reset" title="Reset offset">↺</button>
          </span>
          <span class="vmc-toolbar-spacer"></span>
          <button class="vmc-tbtn vmc-btn-freeze" title="Save current frame as JPEG" data-tour="viewer.freeze" disabled>📷 Freeze</button>
          ${frames}
          <button class="vmc-tbtn vmc-btn-poi" title="Mark a POI at heli position" data-tour="viewer.poi" disabled>📍 POI</button>
          <button class="vmc-tbtn vmc-btn-pin" title="Drop a pin anywhere" data-tour="viewer.pin" disabled>📌 Pin</button>
        </div>
      </div>
      <div class="vmc-divider" role="separator" aria-orientation="vertical"
           tabindex="0" title="Drag to resize · double-click to reset" data-tour="viewer.divider"></div>
      <div class="vmc-map-wrapper"><div class="vmc-map" data-tour="viewer.map"></div></div>
    </div>
    <div class="vmc-error" hidden></div>
  `;
}

export function mountViewer(container, opts) {
  if (!container || !(container instanceof Element)) {
    throw new Error("mountViewer: container must be a DOM Element");
  }
  if (!opts || typeof opts.videoSrc !== "string") {
    throw new Error("mountViewer: opts.videoSrc is required");
  }
  if (!opts.meta && !opts.metaSrc) {
    throw new Error("mountViewer: opts.meta or opts.metaSrc is required");
  }

  const _tileTemplates = resolveTileTemplates(opts.tileTemplates);
  const _baseTileLayers = opts.tileLayers ?? DEFAULT_TILE_LAYERS;
  // When tileTemplates is supplied (offline bundle), patch the URL strings of
  // the street and satellite layers while preserving all other layer config
  // (attribution, subdomains, zoom caps, labels). This keeps the change
  // purely additive — callers that don't pass tileTemplates get identical
  // behaviour to before.
  const tileLayers = opts.tileTemplates
    ? {
        ..._baseTileLayers,
        street: { ..._baseTileLayers.street, url: _tileTemplates.map },
        satellite: { ..._baseTileLayers.satellite, url: _tileTemplates.satellite },
      }
    : _baseTileLayers;
  const currentReviewer = opts.currentReviewer ?? null;
  const REVIEWER_ID = currentReviewer?.reviewerId ?? null;
  const cleanup = []; // pushed disposers, drained by dispose()
  let zoomCtl = null; // assigned by the zoom block below; read by captureFrame
  let panoHost = null; // assigned by the panorama block; read by the Freeze dispatcher
  let disposed = false; // set by dispose(); the async setup IIFE bails on it
  // Set by handle.seekTo(); an explicit seek (e.g. a Saved-Frames ?t= deep
  // link) always wins over the playhead-persistence restore below.
  let explicitSeekRequested = false;

  // Ownership / admin gates live in viewer.popups.js so popup builders and
  // marker handlers share one source of truth. Bind to currentReviewer here.
  function isOwn(item) {
    return popupsIsOwn(item, currentReviewer);
  }

  // Used at serialize time on pin/POI write. If the item is brand-new
  // (no createdBy) or carries a legacy per-browser UUID, stamp the active
  // reviewer's identity onto it so the next R2 round-trip migrates the
  // attribution to a stable hash. Otherwise preserve the existing owner.
  function ownerOnSerialize(item) {
    if (!item.createdBy || isLegacyOrphan(item)) {
      return currentReviewer
        ? { reviewerId: currentReviewer.reviewerId, displayName: currentReviewer.displayName }
        : undefined;
    }
    return item.createdBy;
  }

  // Build chrome. The Saved Frames button is the one host-conditional part:
  // only the cloud shell passes an onOpenFrames handler.
  container.classList.add("vmc-viewer");
  container.innerHTML = buildChromeHTML({
    framesBtn: typeof opts.onOpenFrames === "function",
    framesCount: (opts.initialFrames ?? []).length,
  });

  const els = {
    header: container.querySelector(".vmc-flight-name"),
    video:  container.querySelector(".vmc-video"),
    map:    container.querySelector(".vmc-map"),
    info:   container.querySelector(".vmc-info-bar"),
    error:  container.querySelector(".vmc-error"),
  };

  // Custom video controls (spec 2026-05-26). Replaces native <video controls>
  // to fix Safari's full-frame darkening overlay and keep the bar zoom-stable.
  const clipEl = container.querySelector(".vmc-video-clip");
  const controlsHandle = mountControls(els.video, {
    clip: clipEl,
    previewSrc: typeof opts.previewSrc === "string" ? opts.previewSrc : null,
  });
  cleanup.push(() => controlsHandle.dispose());

  // ?debug=1: passive seek-timing overlay. No-op when absent. See spec §6.
  if (typeof window !== "undefined") {
    const search = window.location?.search ?? "";
    if (/[?&]debug=1(?:&|$)/.test(search)) {
      const probe = createSeekTimingProbe({ maxSamples: 10 });
      const overlay = document.createElement("div");
      overlay.className = "vmc-debug-overlay";
      overlay.setAttribute("role", "status");
      overlay.style.cssText = [
        "position:absolute", "top:8px", "left:8px", "z-index:9999",
        "background:rgba(0,0,0,0.75)", "color:#0f0",
        "font:11px/1.35 ui-monospace,Menlo,monospace",
        "padding:6px 8px", "border-radius:4px",
        "max-width:220px", "pointer-events:none",
        "white-space:pre",
      ].join(";");
      overlay.textContent = "debug: 0 seeks";
      container.appendChild(overlay);

      function render() {
        const s = probe.samples();
        if (s.length === 0) {
          overlay.textContent = "debug: 0 seeks";
          return;
        }
        const recent = s.slice(-10).map((ms) => `${Math.round(ms)}ms`).join(" ");
        overlay.textContent =
          `debug seeks (n=${s.length}, median=${Math.round(probe.getMedian())}ms)\n${recent}`;
      }

      const onSeeking = () => { probe.onSeeking(performance.now()); render(); };
      const onSeeked  = () => { probe.onSeeked(performance.now());  render(); };
      els.video.addEventListener("seeking", onSeeking);
      els.video.addEventListener("seeked",  onSeeked);
      cleanup.push(() => {
        els.video.removeEventListener("seeking", onSeeking);
        els.video.removeEventListener("seeked",  onSeeked);
        overlay.remove();
      });
    }
  }

  // Video zoom (CSS transform). Forward-compatible with future 4K source.
  // See docs/superpowers/specs/2026-05-11-smooth-scrub-phase-a-design.md §6A.
  // Listeners + badge attach to .vmc-video-clip (the new clip wrapper), NOT
  // .vmc-video-panel, so the info-bar + toolbar (siblings of the clip) are
  // unaffected by the zoom transform and stay anchored in the panel column.
  {
    const clip = container.querySelector(".vmc-video-clip");
    if (clip !== null) {
      zoomCtl = createZoomController();
      const badge = document.createElement("div");
      badge.className = "vmc-zoom-badge";
      badge.hidden = true;
      clip.appendChild(badge);

      function applyTransform() {
        const { zoom, tx, ty } = zoomCtl.getState();
        els.video.style.transform = `translate(${tx}px, ${ty}px) scale(${zoom})`;
        if (zoom > 1.001) {
          badge.hidden = false;
          badge.textContent = `${zoom.toFixed(1)}×`;
          clip.classList.add("vmc-zoomed");
        } else {
          badge.hidden = true;
          clip.classList.remove("vmc-zoomed");
        }
      }

      const onWheel = (e) => {
        e.preventDefault();
        const r = clip.getBoundingClientRect();
        zoomCtl.onWheel({
          deltaY: e.deltaY,
          cursorX: e.clientX - r.left,
          cursorY: e.clientY - r.top,
          viewport: { width: r.width, height: r.height },
        });
        applyTransform();
      };

      let dragging = false;
      const onMouseDown = (e) => {
        if (e.button !== 0) return;
        if (!isPanGestureTarget(e.target)) return;
        const r = clip.getBoundingClientRect();
        zoomCtl.onDragStart({
          x: e.clientX - r.left,
          y: e.clientY - r.top,
          viewport: { width: r.width, height: r.height },
        });
        if (zoomCtl.getState().zoom > 1.001) {
          dragging = true;
          clip.classList.add("vmc-zoom-dragging");
          e.preventDefault();
        }
      };
      const onMouseMove = (e) => {
        if (!dragging) return;
        const r = clip.getBoundingClientRect();
        zoomCtl.onDragMove({
          x: e.clientX - r.left,
          y: e.clientY - r.top,
          viewport: { width: r.width, height: r.height },
        });
        applyTransform();
      };
      const onMouseUp = () => {
        if (!dragging) return;
        const startedPan = zoomCtl.onDragEnd();
        dragging = false;
        clip.classList.remove("vmc-zoom-dragging");
        if (startedPan) {
          // The browser still fires a click on the clip after a pan (mousedown
          // and mouseup share a target). Swallow exactly that one click in the
          // capture phase so the controls' play/pause handler never sees it.
          // This fires for every click on a zoomed frame, not just ones that
          // moved: at zoom > 1 the picture is a pan surface, and a reviewer
          // lining up a POI must not toggle playback by clicking it.
          const swallowClick = (e) => { e.stopPropagation(); e.preventDefault(); };
          clip.addEventListener("click", swallowClick, { capture: true, once: true });
          // If the mouseup landed outside the clip no click reaches it — the
          // trailing click (if any) dispatches before timers run, so a 0 ms
          // disarm never swallows a later legitimate click.
          setTimeout(() => {
            clip.removeEventListener("click", swallowClick, { capture: true });
          }, 0);
        }
      };
      const onDblClick = (e) => {
        e.preventDefault();
        zoomCtl.reset();
        applyTransform();
      };

      clip.addEventListener("wheel", onWheel, { passive: false });
      clip.addEventListener("mousedown", onMouseDown);
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup",   onMouseUp);
      clip.addEventListener("dblclick", onDblClick);
      cleanup.push(() => {
        clip.removeEventListener("wheel", onWheel);
        clip.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup",   onMouseUp);
        clip.removeEventListener("dblclick", onDblClick);
        badge.remove();
        els.video.style.transform = "";
        clip.classList.remove("vmc-zoomed", "vmc-zoom-dragging");
      });
    }
  }

  function showError(msg) {
    els.error.hidden = false;
    els.error.textContent = msg;
    console.error("[viewer]", msg);
    hideLoading();
  }

  // --- Loading overlay: paint immediately so users see "Loading map…"
  // during the slow MP4 telemetry parse, not just during tile fetches.
  // Hidden when either (a) the active basemap fires `load`, or (b) 12 s
  // elapse — whichever first. Tile servers occasionally hang on a single
  // tile and never finish; don't pin the overlay forever.
  const loadingEl = document.createElement("div");
  loadingEl.className = "vmc-map-loading";
  loadingEl.textContent = "Loading map…";
  els.map.parentElement.appendChild(loadingEl);
  let loadingDone = false;
  const hideLoading = () => {
    if (loadingDone) return;
    loadingDone = true;
    loadingEl.remove();
  };
  const loadingTimer = setTimeout(hideLoading, 12000);
  cleanup.push(() => { clearTimeout(loadingTimer); loadingEl.remove(); });

  // Resolve meta — accept pre-loaded object or fetch from URL.
  async function loadMeta() {
    if (opts.meta) return opts.meta;
    const r = await fetch(opts.metaSrc);
    if (!r.ok) throw new Error(`meta.json fetch failed: ${r.status}`);
    return r.json();
  }

  // Hand back the control surface (declared early so the IIFE can patch it).
  const handle = {
    setPois(newPois) { handle._setPois?.(newPois); },
    setPins(newPins) { handle._setPins?.(newPins); },
    setFrames(frames) { handle._setFrames?.(frames); },
    seekTo(seconds) {
      if (typeof seconds !== "number" || !els.video) return;
      explicitSeekRequested = true;
      // A deep-link seek can arrive before the video has loaded its metadata;
      // setting currentTime then is silently lost. Defer until metadata is in.
      if (els.video.readyState >= 1 /* HAVE_METADATA */) {
        els.video.currentTime = seconds;
      } else {
        els.video.addEventListener(
          "loadedmetadata",
          () => { els.video.currentTime = seconds; },
          { once: true },
        );
      }
    },
    /** Pause playback if the video exists; no-op otherwise. Used by the cloud
     *  tour so the auto-hiding controls bar stays visible (spec 2026-09-04 D9). */
    pause() {
      if (els.video && typeof els.video.pause === "function") els.video.pause();
    },
    /** Redraw the basemaps from `set` — the host's tile guard tripped and the
     *  paid provider is off the table for the rest of this page. Safe before
     *  the map exists: the set is stashed and drained at map setup, so no
     *  keyed tile is ever requested. */
    degradeTiles(set) {
      applyOrStashDegrade(handle, mapHandles, set);
    },
    openPanorama(panoramaId, view) {
      // _openPanorama is patched late, deep in the async setup IIFE below. If a
      // deep-link arrives before then, stash it; the patch site drains it.
      if (handle._openPanorama) handle._openPanorama(panoramaId, view);
      else handle._pendingPanorama = { panoramaId, view };
    },
    setOverlayVisibility(id, visible) {
      handle._setOverlayVisibility?.(id, visible);
    },
    addOverlay(id, fc) {
      // Patched late like the rest of the map-bound calls. An add before the
      // map exists is stashed and drained at the patch site; the layer starts
      // hidden either way, so visibility is still React's call.
      if (handle._addOverlay) handle._addOverlay(id, fc);
      else {
        if (!handle._pendingOverlays) handle._pendingOverlays = new Map();
        handle._pendingOverlays.set(id, fc);
      }
    },
    removeOverlay(id) {
      if (handle._removeOverlay) handle._removeOverlay(id);
      else if (handle._pendingOverlays) handle._pendingOverlays.delete(id);
    },
    setGhostTracks(tracks) {
      // Patched late (the map exists only after telemetry resolves). A set
      // arriving before then is stashed and drained at the patch site —
      // same pending pattern as _pendingPanorama.
      if (handle._setGhostTracks) handle._setGhostTracks(tracks);
      else handle._pendingGhostTracks = tracks;
    },
    dispose() {
      disposed = true;
      while (cleanup.length) {
        try { cleanup.pop()(); } catch (e) { console.warn(e); }
      }
      container.classList.remove("vmc-viewer");
      container.innerHTML = "";
    },
  };

  // Bootstrap.
  let meta = null;
  let telemetry = [];
  let mapHandles = null;
  // Start video buffering immediately — independent of meta/telemetry fetches.
  // crossOrigin must be set BEFORE src so the fetch carries the CORS request.
  // Cloud hosts pass "anonymous" so canvas reads work; HDD passes nothing.
  if (typeof opts.videoCrossOrigin === "string") {
    els.video.crossOrigin = opts.videoCrossOrigin;
  }
  // ABR (cloud): attach hls.js. Progressive (HDD / legacy): plain src.
  // Held at function scope: D3 (scrub preview) and D4 (quality menu) need it.
  let hls = null;
  if (typeof opts.hlsSrc === "string") {
    const Hls = globalThis.Hls;
    if (!Hls) {
      throw new Error("globalThis.Hls not found — load hls.js before viewer.js");
    }
    if (Hls.isSupported()) {
      hls = new Hls({
        // Attach the team JWT only to Worker-origin requests (the HLS
        // manifests). Presigned R2 segment URLs are a different origin and
        // carry their own query-string auth — they must NOT get the header.
        xhrSetup: (xhr, url) => {
          if (typeof opts.authToken === "string" && isWorkerUrl(url, opts.workerOrigin)) {
            xhr.setRequestHeader("Authorization", `Bearer ${opts.authToken}`);
          }
        },
      });
      hls.loadSource(opts.hlsSrc);
      hls.attachMedia(els.video);
      cleanup.push(() => hls.destroy());
      // Auto never climbs past the cap (55 Mbps); anything above it is a
      // manual-only Max behind a one-time confirm. Rules in viewer.quality.js.
      const qualitySel = container.querySelector(".vmc-quality");
      // Beside the menu, the level Auto is playing right now ("4K"), so a
      // reviewer who needs to know never has to guess. Blank on a manual
      // choice, since the menu itself then says which.
      const qualityNow = container.querySelector(".vmc-quality-now");
      const paintQualityNow = () => {
        const text = autoLevelBadge(hls.levels, hls.currentLevel, hls.autoLevelEnabled);
        qualityNow.textContent = text;
        qualityNow.hidden = text === "";
      };
      hls.on(Hls.Events.LEVEL_SWITCHED, paintQualityNow);
      let maxConsented = false;
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        const cap = autoCapIndex(hls.levels);
        if (cap >= 0) hls.autoLevelCapping = cap;
        // Clear any options from a prior MANIFEST_PARSED (keep Auto at index 0).
        while (qualitySel.options.length > 1) qualitySel.remove(1);
        // Auto + one option per level, in level order. hls.js has already
        // dropped any level this browser cannot decode (the H.265 rung on
        // Firefox), so the menu only ever offers what will play.
        hls.levels.forEach((l, i) => {
          const opt = document.createElement("option");
          opt.value = String(i);
          opt.textContent = levelLabel(l, hls.levels);
          qualitySel.appendChild(opt);
        });
        qualitySel.hidden = false;
      });
      qualitySel.addEventListener("change", () => {
        const idx = parseInt(qualitySel.value, 10);
        const level = idx >= 0 ? hls.levels[idx] : null;
        if (level && isMaxLevel(level) && !maxConsented) {
          const ok = window.confirm(
            "Maximum quality needs ~80 Mbps of sustained bandwidth and makes " +
            "seeking noticeably slower. Continue?",
          );
          if (!ok) { qualitySel.value = "-1"; hls.currentLevel = -1; return; }
          maxConsented = true;
        }
        hls.currentLevel = idx;       // -1 = auto
        paintQualityNow();
      });
    } else if (els.video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari plays HLS natively; the manifest URL is fetched by the element.
      els.video.src = opts.hlsSrc;
    } else {
      showError("HLS is not supported in this browser.");
    }
  } else {
    els.video.src = opts.videoSrc;
  }
  // Safari otherwise renders the FMV's embedded telemetry subtitle track
  // over the picture, and our control bar blocks the native controls that
  // would switch it off.
  suppressTextTracks(els.video);

  // Flight-set autoplay chaining (Plan 13): React decides what "next" means;
  // the viewer only reports that this clip reached its natural end.
  if (typeof opts.onVideoEnded === "function") {
    const onEnded = () => opts.onVideoEnded();
    els.video.addEventListener("ended", onEnded);
    cleanup.push(() => els.video.removeEventListener("ended", onEnded));
  }

  // Seat presence (Plan 20): React needs "a video is playing" for the
  // heartbeat, since watching counts as activity.
  if (typeof opts.onPlayStateChange === "function") {
    const onPlay = () => opts.onPlayStateChange(true);
    const onStop = () => opts.onPlayStateChange(false);
    els.video.addEventListener("play", onPlay);
    els.video.addEventListener("pause", onStop);
    els.video.addEventListener("ended", onStop);
    cleanup.push(() => {
      els.video.removeEventListener("play", onPlay);
      els.video.removeEventListener("pause", onStop);
      els.video.removeEventListener("ended", onStop);
    });
  }

  // Smooth-scrub preview source. The .vmc-scrubbing visibility toggling
  // (and pointer-driven seek of preview.currentTime) now lives in
  // viewer.controls.js, driven by the custom scrub bar.
  if (typeof opts.previewSrc === "string") {
    const preview = container.querySelector(".vmc-preview");
    preview.src = opts.previewSrc;
    if (typeof opts.videoCrossOrigin === "string") {
      preview.crossOrigin = opts.videoCrossOrigin;
    }
    // Legacy HDD bundles ship no preview.mp4 and the shell passes the path
    // unconditionally: on load error, drop to no-preview behaviour instead of
    // showing a black .vmc-preview box while scrubbing. (controls' writes to
    // the detached element are already try/caught.)
    preview.addEventListener("error", () => { preview.remove(); }, { once: true });
  } else {
    // No preview asset for this flight (legacy progressive publish): remove
    // the element so .vmc-scrubbing never reveals a src-less black box.
    container.querySelector(".vmc-preview")?.remove();
  }

  (async () => {
    try {
      // meta + telemetry can fetch in parallel.
      const [metaResolved, telemetryResolved] = await Promise.all([
        loadMeta(),
        opts.trackSrc
          ? loadTrackJson(opts.trackSrc)
          : extractTelemetry(opts.videoSrc),
      ]);
      meta = metaResolved;
      telemetry = telemetryResolved;
      // If dispose() ran while the fetches were in flight, abort the rest of
      // setup — the DOM is gone and `container` may now belong to a newer
      // viewer instance. Continuing here would attach this (dead) viewer's
      // listeners onto the live one.
      if (disposed) return;
      // Cadence-derived stride: aim for ~1 s of baseline between the two
      // samples used for bearing + drag projection, regardless of native
      // capture rate (1 Hz GPX, 24 Hz VFX, 5 Hz Hawk INS, etc.). At 1 Hz
      // this resolves to stride=1 (identical to pre-Plan-7 behaviour).
      const HEADING_BASELINE_S = 1.0;
      const medianDt = medianDeltaSeconds(telemetry);
      const headingStride = Math.max(
        1, Math.round(HEADING_BASELINE_S / Math.max(0.001, medianDt)),
      );
      // Smooth the displayed lat/lon with a 1-second centered moving
      // average (= ``headingStride`` samples). This kills the GPS noise
      // that causes:
      //   - the heli icon to wobble every rAF tick at 24 Hz, and
      //   - the drag's screen-pixel segment projection to snag on the
      //     "mini sharp turns" introduced by sub-metre GPS jitter.
      // ``t`` is untouched so seeking + frame capture remain sample-
      // accurate; only the rendered position is smoothed. At 1 Hz
      // (``headingStride === 1``) this is a no-op — legacy behaviour
      // is preserved exactly.
      smoothPositionsInPlace(telemetry, headingStride);
      els.header.textContent = meta.name || meta.flightId || "(unnamed flight)";
      console.info(`[viewer] loaded ${telemetry.length} telemetry samples ` +
        `(via ${opts.trackSrc ? "track.json" : "mp4box"})`);
      // --- Playhead persistence: restore where the reviewer left off, then
      // keep the stored position fresh while they watch. An explicit
      // handle.seekTo() (the Saved-Frames ?t= deep link, applied by
      // Viewer.tsx synchronously in the same mount effect flush, before this
      // IIFE resumes past its first await) always wins — it sets
      // `explicitSeekRequested`, which restore() checks first and bails on.
      // HDD file:// shells with blocked storage no-op silently.
      if (meta.flightId) {
        const saver = createPlayheadSaver(meta.flightId);
        const onTimeUpdate = () => saver.onTimeUpdate(els.video.currentTime);
        const onSeeked = () => saver.flush(els.video.currentTime);
        const onPageHide = () => saver.flush(els.video.currentTime);
        els.video.addEventListener("timeupdate", onTimeUpdate);
        els.video.addEventListener("seeked", onSeeked);
        window.addEventListener("pagehide", onPageHide);
        cleanup.push(() => {
          els.video.removeEventListener("timeupdate", onTimeUpdate);
          els.video.removeEventListener("seeked", onSeeked);
          window.removeEventListener("pagehide", onPageHide);
        });
        const restore = () => {
          if (disposed || explicitSeekRequested) return;
          const saved = loadPlayhead(meta.flightId, els.video.duration);
          if (saved !== null) els.video.currentTime = saved;
        };
        if (els.video.readyState >= 1 /* HAVE_METADATA */) {
          restore();
        } else {
          els.video.addEventListener("loadedmetadata", restore, { once: true });
          cleanup.push(() => els.video.removeEventListener("loadedmetadata", restore));
        }
      }
      // Toolbar gating: enable telemetry-dependent buttons once both
      // (a) telemetry resolved (above) and (b) <video> has decodable data.
      const enableToolbar = () => {
        for (const sel of [
          ".vmc-btn-freeze",
          ".vmc-btn-poi",
          ".vmc-btn-pin",
          "#vmc-frames-btn",
        ]) {
          const btn = container.querySelector(sel);
          if (btn) btn.removeAttribute("disabled");
        }
      };
      if (els.video.readyState >= 2 /* HAVE_CURRENT_DATA */) {
        enableToolbar();
      } else {
        els.video.addEventListener("loadeddata", enableToolbar, { once: true });
      }
      mapHandles = mountMap(els.map, telemetry, tileLayers, cleanup, opts.onTileLoad);
      // A degrade that arrived before the map existed (the host's tile guard
      // had already tripped) is applied here — before any layer is added, so
      // the keyed provider is never asked for a tile.
      applyOrStashDegrade(handle, mapHandles, null);
      const { map, L, street, satellite, satLabels } = mapHandles;
      const dividerHandle = mountDivider(container, { map });
      cleanup.push(() => dividerHandle.dispose());
      const initialHeadingEnds = macroSegmentEndpoints(
        0, headingStride, telemetry.length,
      );
      const initialHeading = telemetry.length > 1
        ? bearing(telemetry[initialHeadingEnds.aIdx], telemetry[initialHeadingEnds.bIdx])
        : 0;
      const heliMarker = L.marker(
        [telemetry[0].lat, telemetry[0].lon],
        // zIndexOffset 1200 keeps the heli above POIs (900), pins, and the
        // panorama markers (1100) so it is never hidden behind them.
        { icon: makeHeliIcon(L, initialHeading), draggable: true, zIndexOffset: 1200 },
      ).addTo(map);
      mapHandles.heli = heliMarker;
      cleanup.push(() => map.removeLayer(heliMarker));

      // Last-known heli pose, kept fresh by the rAF sync loop and the
      // drag-scrub handler. The Follow control snaps to it on activation
      // (the video may be paused, so a tick can't be waited on).
      let lastHeliPos = telemetry.length
        ? { lat: telemetry[0].lat, lon: telemetry[0].lon, heading: initialHeading }
        : null;
      // leaflet-rotate keeps marker icons screen-upright (norotatePane), so
      // the heli icon's own rotation must add the map bearing back in: a
      // geographic heading H sits at screen angle H + bearing — which is 0
      // while heading-up follow keeps bearing = -H, and plain H (bearing 0)
      // otherwise. Repainted on every map `rotate` so a bearing change with
      // the video paused still reorients the icon.
      function paintHeliHeading() {
        if (lastHeliPos?.heading == null) return;
        const wrap = heliMarker.getElement()?.querySelector(".vmc-heli-wrap");
        if (!wrap) return;
        const mapBearing = map.getBearing?.() ?? 0;
        wrap.style.transform = `rotate(${lastHeliPos.heading + mapBearing}deg)`;
      }
      map.on("rotate", paintHeliHeading);
      cleanup.push(() => map.off("rotate", paintHeliHeading));

      // --- Initial basemap: satellite + labels (default since 2026-05-05).
      satellite.addTo(map);
      satLabels?.addTo(map);
      let isSat = true;
      // Hide the loading overlay once the satellite layer's first batch of
      // tiles finishes. The 12 s timeout above is the backstop.
      satellite.once("load", hideLoading);

      // --- Basemap swap swatch: 56x72 thumbnail of the *other* basemap,
      // captioned beneath. The swatch's job is to convey "satellite vs map"
      // at a glance — it doesn't need to match the flight area. Use a
      // fixed iconic tile (Sydney Harbour at z12) so both previews show
      // clear water + dense road grid + a recognisable shoreline,
      // independent of where the flight is.
      const PREVIEW_LAT = -33.8568;
      const PREVIEW_LON = 151.2153;
      const PREVIEW_Z = 12;
      const streetPreviewUrl = tilePreviewUrl(
        tileLayers.street.url, PREVIEW_LAT, PREVIEW_LON, PREVIEW_Z,
        { zoomOffset: tileLayers.street.zoomOffset ?? 0 },
      );
      const satPreviewUrl = tilePreviewUrl(
        tileLayers.satellite.url, PREVIEW_LAT, PREVIEW_LON, PREVIEW_Z,
        { zoomOffset: tileLayers.satellite.zoomOffset ?? 0 },
      );
      // Map-corner controls. Top-right: the Hawk-style navigation rail
      // (compass, follow toggles, footprint). Bottom-right: the basemap
      // swatch, kept clear of Leaflet's attribution line.
      const corner = document.createElement("div");
      corner.className = "vmc-map-corner";
      els.map.parentElement.insertBefore(corner, els.map);
      cleanup.push(() => corner.remove());
      const cornerBR = document.createElement("div");
      cornerBR.className = "vmc-map-corner-br";
      els.map.parentElement.insertBefore(cornerBR, els.map);
      cleanup.push(() => cornerBR.remove());
      const swatch = document.createElement("button");
      swatch.className = "vmc-basemap-swatch";
      swatch.title = "Switch basemap";
      swatch.dataset.tour = "viewer.basemap";
      const swatchImg = document.createElement("div");
      swatchImg.className = "vmc-basemap-swatch-img";
      const swatchLabel = document.createElement("div");
      swatchLabel.className = "vmc-basemap-swatch-label";
      swatch.append(swatchImg, swatchLabel);
      cornerBR.appendChild(swatch);
      function paintSwatch() {
        // Show the basemap we'd switch *to*, not the current one.
        if (isSat) {
          swatchImg.style.backgroundImage = `url("${streetPreviewUrl}")`;
          swatchLabel.textContent = "Map";
        } else {
          swatchImg.style.backgroundImage = `url("${satPreviewUrl}")`;
          swatchLabel.textContent = "Satellite";
        }
      }
      paintSwatch();
      // At fractional zoom levels (zoomSnap 0.5) the browser scales tiles by
      // a non-integer factor and hairline gaps open between them, showing the
      // map background as a grid. The gaps can't be removed, so keep the
      // background matched to the active basemap: near-black under satellite
      // imagery, Leaflet's stock light grey under the street map. The CSS
      // default (styles.css .vmc-map) covers the initial satellite state.
      function paintMapBackground() {
        els.map.style.background = isSat ? "#0b0b0b" : "#ddd";
      }
      swatch.addEventListener("click", () => {
        isSat = !isSat;
        if (isSat) {
          map.removeLayer(street);
          satellite.addTo(map);
          satLabels?.addTo(map);
        } else {
          map.removeLayer(satellite);
          satLabels && map.removeLayer(satLabels);
          street.addTo(map);
        }
        paintSwatch();
        paintMapBackground();
      });
      cleanup.push(() => swatch.remove());

      // Navigation rail: compass + follow toggles (Hawk-style). The rAF
      // sync loop feeds it via followCtl.update(); a user drag on the map
      // cancels follow (handled inside the module).
      const followCtl = mountFollowControl(corner, {
        map,
        getHeliState: () => lastHeliPos,
      });
      cleanup.push(() => followCtl.dispose());

      // --- Client reference overlays (per-team KMZs). opts.overlays is an
      // array of { id, fc } parsed in web/. One hidden L.geoJSON layer per
      // id; React drives visibility via handle.setOverlayVisibility(id, on)
      // and, since overlays can be added from the website while a flight is
      // open (spec 2026-09-09), adds and removes layers through
      // handle.addOverlay / removeOverlay. Unlike the footprint below, the
      // control lives in the React header (Plan 12), so there is no in-map
      // affordance mounted here.
      const overlays = createOverlayRegistry(L, map, opts.overlays);
      cleanup.push(() => overlays.dispose());
      handle._setOverlayVisibility = (id, visible) => overlays.setVisible(id, visible);
      handle._addOverlay = (id, fc) => overlays.add(id, fc);
      handle._removeOverlay = (id) => overlays.remove(id);
      // Adds that arrived before the map existed were stashed by the handle.
      if (handle._pendingOverlays) {
        for (const [id, fc] of handle._pendingOverlays) overlays.add(id, fc);
        handle._pendingOverlays = null;
      }

      // --- Flight-set ghost tracks (Plan 13). Grey sibling tracks beneath
      // the red active track; click navigates (callback owned by React).
      // Unlike the overlays above, sibling tracks are fetched after mount, so
      // React drives them through handle.setGhostTracks() and an arrival
      // before this patch site is stashed on handle._pendingGhostTracks.
      let ghostLayers = [];
      const clearGhostLayers = () => {
        for (const lyr of ghostLayers) {
          if (map.hasLayer(lyr)) map.removeLayer(lyr);
        }
        ghostLayers = [];
      };
      cleanup.push(clearGhostLayers);
      handle._setGhostTracks = (tracks) => {
        clearGhostLayers();
        ghostLayers = buildGhostLayers(L, tracks, opts.onGhostTrackClick);
        for (const lyr of ghostLayers) lyr.addTo(map);
        // Ghosts share the map's single overlayPane SVG renderer with the
        // red track, so paint order is DOM order within that renderer.
        // bringToBack() in REVERSE build order: the sweep ends with the
        // first-built layer bottom-most, restoring build order (each ghost's
        // casing under its top stroke) while keeping every ghost beneath the
        // track without demoting it below the basemap (a different pane).
        // The footprint has its own pane at z-index 410, so it stays above
        // these regardless.
        for (let i = ghostLayers.length - 1; i >= 0; i--) ghostLayers[i].bringToBack();
      };
      if (handle._pendingGhostTracks) {
        handle._setGhostTracks(handle._pendingGhostTracks);
        handle._pendingGhostTracks = null;
      }

      // Operator GPS-sync nudge. `syncT` maps video time onto telemetry time
      // and is THE time base for every telemetry-driven surface (heli marker,
      // info bar, freeze-frame caption, footprint) — declared here so the
      // footprint controller below can be handed the same function.
      let gpsOffsetS = 0;
      const syncT = (vt) => vt + gpsOffsetS;

      // Footprint overlay (Plan 7) — only when meta has a footprint block
      // and the caller supplied a fetch URL.
      let footprintCtl = null;
      if (meta?.footprint && opts.footprintUrl) {
        footprintCtl = createFootprintController({
          L,
          map,
          video: els.video,
          fetchUrl: opts.footprintUrl,
          syncT,
        });
        cleanup.push(() => {
          footprintCtl?.destroy();
          footprintCtl = null;
        });
      }
      // The spec's "Footprint" layer-control entry, translated to this
      // viewer's own idiom: a map-corner button under the basemap swatch.
      // No-ops when the controller is null, so a flight without a sidecar
      // shows no affordance.
      // The footprint toggle joins the same rail so the corner stays one
      // aligned Hawk-style button group.
      const footprintToggle = mountFootprintToggle(followCtl.railEl ?? corner, {
        controller: footprintCtl,
      });
      cleanup.push(() => footprintToggle.dispose());

      const videoStartUtcSec = new Date(meta.videoStartUtc).getTime() / 1000;
      const tz = meta.displayTimezone || opts.defaultTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

      els.info.innerHTML = `
        <span><span class="vmc-lbl">Video </span><span class="vmc-val vmc-vid-time">0:00.0</span></span>
        <span><span class="vmc-lbl">Local </span><span class="vmc-val vmc-local-time">—</span></span>
        <span><span class="vmc-lbl">UTC </span><span class="vmc-val vmc-utc-time">—</span></span>
        <span><span class="vmc-lbl">Lat </span><span class="vmc-val-g vmc-lat">—</span></span>
        <span><span class="vmc-lbl">Lon </span><span class="vmc-val-g vmc-lon">—</span></span>
        <span><span class="vmc-lbl">Alt </span><span class="vmc-val-g vmc-alt">—</span></span>
        <span><span class="vmc-lbl">Hdg </span><span class="vmc-val-g vmc-hdg">—</span></span>
        <span><span class="vmc-lbl">±GPS </span><span class="vmc-val vmc-gps-delta">—</span></span>
      `;
      const ib = {
        vidTime: els.info.querySelector(".vmc-vid-time"),
        localTime: els.info.querySelector(".vmc-local-time"),
        utcTime: els.info.querySelector(".vmc-utc-time"),
        lat: els.info.querySelector(".vmc-lat"),
        lon: els.info.querySelector(".vmc-lon"),
        alt: els.info.querySelector(".vmc-alt"),
        hdg: els.info.querySelector(".vmc-hdg"),
        gpsDelta: els.info.querySelector(".vmc-gps-delta"),
      };

      const offsetVal = container.querySelector(".vmc-offset-val");
      function setOffset(delta) {
        gpsOffsetS = Math.round((gpsOffsetS + delta) * 10) / 10;
        offsetVal.textContent = (gpsOffsetS >= 0 ? "+" : "") + gpsOffsetS.toFixed(1) + "s";
        offsetVal.style.color = gpsOffsetS === 0 ? "" : "#fc8";
        lastT = -1; // force rAF refresh
        // The rAF loop above only moves the heli marker; the footprint listens
        // to video events, which a nudge on a paused video never fires.
        footprintCtl?.refresh();
      }
      container.querySelector(".vmc-sync-down").addEventListener("click", () => setOffset(-0.5));
      container.querySelector(".vmc-sync-up").addEventListener("click", () => setOffset(0.5));
      container.querySelector(".vmc-sync-reset").addEventListener("click", () => {
        gpsOffsetS = 0; setOffset(0);
      });

      let canvasOK = false;
      function testCanvas() {
        if (els.video.readyState < 2) return;
        try {
          const tc = document.createElement("canvas");
          tc.width = 1; tc.height = 1;
          tc.getContext("2d").drawImage(els.video, 0, 0, 1, 1);
          tc.toDataURL();
          canvasOK = true;
        } catch (_e) { canvasOK = false; }
      }
      els.video.addEventListener("loadeddata", testCanvas);

      function downloadBlob(blob, filename) {
        const a = document.createElement("a");
        const url = URL.createObjectURL(blob);
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }

      // Burn the caption (local date-time, geotag, flight id) into a capture.
      // Kept here (not in viewer.freeze.capture.js) because it closes over
      // `tz` and `meta`; emitPanoFrame still calls it directly.
      function drawFrameCaption(ctx, canvas, { utcS, position }) {
        drawCaptionLines(ctx, canvas.width, canvas.height, buildCaptionLines({
          utcS, tz, position, flightId: meta.flightId,
        }));
      }

      // Helper: format video time as filename-safe string.
      function fmtTimecode(seconds) {
        const t = videoStartUtcSec + syncT(seconds);
        return fmtClock(t, tz).replace(/[: ]/g, "_");
      }

      // Under ABR, ensure the <video> is showing a 4K rung before a capture
      // so freeze-frames are full-resolution Package C deliverables. No-op on
      // the HDD/progressive path (no hls, no rung switching).
      async function ensureFullResForCapture() {
        if (!hls || !Array.isArray(hls.levels)) return;
        if (els.video.videoHeight >= 2160) return;
        // `Hls` (the class) is block-scoped to the D2 attach block; reference
        // it via globalThis. When `hls` is non-null it is guaranteed present.
        const HlsClass = globalThis.Hls;
        const topIdx = topLevelIndex(hls.levels);
        if (topIdx < 0) return;
        // If the top rung is already current, assigning it again fires no
        // LEVEL_SWITCHED — skip the wait (the capture proceeds on the frame
        // shown; it is already top-rung data).
        if (hls.currentLevel === topIdx) return;
        const prevLevel = hls.currentLevel;
        await new Promise((resolve) => {
          let done = false;
          let timer = null;
          const onSeeked = () => finish();
          const onSwitch = (_evt, data) => {
            if (data.level !== topIdx) return;
            hls.off(HlsClass.Events.LEVEL_SWITCHED, onSwitch);
            els.video.addEventListener("seeked", onSeeked, { once: true });
            // Nudge by a tiny epsilon so a `seeked` reliably fires even when
            // the playhead is already at this time (a same-value assignment
            // is a no-op in some browsers and fires no event). Bidirectional
            // so t===0 (first frame) still moves and fires `seeked`.
            const t = els.video.currentTime;
            els.video.currentTime = t > 0.001 ? t - 0.001 : t + 0.001;
          };
          function finish() {
            if (done) return;
            done = true;
            hls.off(HlsClass.Events.LEVEL_SWITCHED, onSwitch);
            els.video.removeEventListener("seeked", onSeeked);
            if (timer !== null) clearTimeout(timer);
            // Restore the user's prior level (incl. -1 for auto). This only
            // schedules an async re-fetch — the 4K frame stays on screen
            // through captureFrame's synchronous drawImage, so the capture is
            // still 4K while playback drifts back to prevLevel afterwards.
            hls.currentLevel = prevLevel;
            resolve();
          }
          // Never hang a capture: resolve anyway after the timeout (degraded
          // — the capture then uses whatever frame is on screen).
          timer = setTimeout(finish, 3000);
          hls.on(HlsClass.Events.LEVEL_SWITCHED, onSwitch);
          hls.currentLevel = topIdx;
        });
      }

      // The Freeze button: the picture on screen, cropped to the zoom.
      async function captureFrame() {
        await ensureFullResForCapture();
        // Re-test on every capture: canvasOK is only set on `loadeddata`, but
        // CORS / readiness state can change after seeks or src swaps. Without
        // this, a stale `false` from an earlier readyState<2 leaves the user
        // with a permanent "tainted" error even when the canvas is fine now.
        if (!canvasOK) testCanvas();
        if (!canvasOK) {
          // Cloud hosts (allowLocalFallback === false) must not be offered the
          // file-picker fallback — picking a local file would replace the
          // streaming video. Cross-origin canvas taint is fixed via
          // videoCrossOrigin + R2 CORS, not by re-loading from disk.
          if (opts.allowLocalFallback === false) {
            showError(
              "Freeze unavailable: video canvas is tainted (cross-origin without CORS).",
            );
            return;
          }
          const input = document.createElement("input");
          input.type = "file"; input.accept = "video/mp4,video/*";
          input.addEventListener("change", () => {
            const f = input.files?.[0]; if (!f) return;
            const cur = els.video.currentTime, paused = els.video.paused;
            els.video.src = URL.createObjectURL(f);
            els.video.addEventListener("loadeddata", function once() {
              els.video.removeEventListener("loadeddata", once);
              els.video.currentTime = cur;
              if (!paused) els.video.play();
              testCanvas();
              if (canvasOK) captureFrame();
            }, { once: true });
          });
          input.click();
          return;
        }

        // Always produce two canvases: one full-res, one 320×180 sidecar thumb.
        const v = els.video;
        const fullW = v.videoWidth, fullH = v.videoHeight;
        if (!fullW || !fullH) return;

        // Crop to the region the reviewer actually sees. At zoom=1 this is the
        // whole frame; when zoomed it is a native-pixel sub-rect (no upscale).
        const z = zoomCtl ? zoomCtl.getState() : { zoom: 1, tx: 0, ty: 0 };
        const clipEl = container.querySelector(".vmc-video-clip");
        const panel = clipEl ? clipEl.getBoundingClientRect() : { width: 0, height: 0 };
        const rect = computeSourceRect({
          videoWidth: fullW, videoHeight: fullH,
          panelWidth: panel.width, panelHeight: panel.height,
          zoom: z.zoom, tx: z.tx, ty: z.ty,
        });
        if (rect === null) {
          showError("Freeze failed: video panel has no layout dimensions.");
          return;
        }
        const outW = Math.max(1, Math.round(rect.sw));
        const outH = Math.max(1, Math.round(rect.sh));

        // Full-res canvas (also used for local download + on-screen overlay text).
        const full = document.createElement("canvas");
        full.width = outW; full.height = outH;
        const fctx = full.getContext("2d");
        fctx.drawImage(v, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, outW, outH);

        await emitVideoFrame(full, { videoTimeS: v.currentTime });
      }

      // A POI's "Save frame": the POI's own moment, however far playback has
      // got since, grabbed from a hidden copy of the video at its top quality
      // level so the video the reviewer is watching never moves. The whole
      // frame: a zoom on the playing video says nothing about another moment.
      // Built on the first press, reused after.
      const poiGrabber = lazyGrabber(() => createVideoGrabber(hls
        ? { manifestUrl: opts.hlsSrc, authToken: opts.authToken, workerOrigin: opts.workerOrigin }
        : { videoUrl: els.video.currentSrc || opts.videoSrc }));
      cleanup.push(() => poiGrabber.dispose());

      async function capturePoiFrame(poi) {
        const videoTimeS = poi.t - gpsOffsetS; // poi.t is gps-time
        let full;
        try {
          full = await poiGrabber.grab(videoTimeS);
        } catch (err) {
          console.warn("Save frame: no picture at the POI's moment", err);
          showError(err instanceof CaptureFatalError
            ? `Couldn't save a frame for this POI. ${err.message}`
            : "Couldn't get a picture at this POI's moment. Try again.");
          return;
        }
        try {
          await emitVideoFrame(full, { videoTimeS, poi });
        } finally {
          // A 4.6K canvas is ~48 MB; let it go now rather than at GC.
          full.width = 0; full.height = 0;
        }
      }

      // What every video frame shares, whoever took the picture: where and
      // when, the burn-in and EXIF, the download and the upload.
      // `videoTimeS` is the moment the picture shows.
      async function emitVideoFrame(full, { videoTimeS, poi = null }) {
        // Where and when. The geotag is the centre of the PICTURE — the
        // footprint's boresight ground hit — not the aircraft: the thing a
        // reviewer froze on is in the frame, and the aircraft is not. That
        // holds for a POI's frame too (a POI itself lands at the aircraft
        // position, so it is no better a subject than the heli).
        // Without a usable centre the aircraft stands in, flagged as such.
        const gpsT = syncT(videoTimeS);
        const sample = interpolate(telemetry, gpsT, headingStride);
        const utcS = videoStartUtcSec + gpsT;
        const position = resolveFramePosition({
          centre: footprintCtl ? footprintCtl.centreAt(gpsT) : null,
          aircraft: sample,
        });
        const rendered = await renderFreezeFrame(full, {
          utcS, tz, flightId: meta.flightId, poiName: poi?.name,
          videoTimeS, position, aircraft: sample,
        });
        if (!rendered) {
          showError("Freeze: could not encode the image.");
          return;
        }

        // Local download (always — matches Hawk_Post and matches HDD UX).
        downloadBlob(rendered.fullBlob, `frame_${fmtTimecode(videoTimeS)}.jpg`);

        // Cloud upload (only if host wired it). Thumb: 16:9 letterbox at 320×180.
        if (typeof opts.onFrameCapture === "function" && rendered.thumbBlob) {
          const frameId = uuid();
          await opts.onFrameCapture(frameId, rendered.fullBlob, rendered.thumbBlob, {
            // The video position "Jump to" seeks back to: the moment shown.
            videoTimeS,
            utc: utcS,
            // Same spot the burn-in and EXIF carry, so the Saved Frames
            // card, the KMZ placemark and the picture agree.
            lat: position?.lat ?? 0,
            lon: position?.lon ?? 0,
            poiId: poi?.id,
          });
        }
      }

      // A panorama's own position is where its camera stood — for a 360°
      // sphere that IS the picture's centre, so it geotags as one.
      function panoPosition(panorama) {
        return resolveFramePosition({ centre: panorama, aircraft: null });
      }

      // Take a captureView() result, burn in the overlay, download locally,
      // and (if wired) upload to the cloud as a panorama-sourced frame.
      async function emitPanoFrame(cap) {
        const full = cap.canvas;
        const panorama = cap.panorama;
        const view = cap.view;
        // Capture timestamp: the panorama's own EXIF time when present, else
        // the moment of capture. Used for both the burn-in and the frame's
        // stored utc so the two always agree.
        const rawUtcS = panorama.capturedAt
          ? Date.parse(panorama.capturedAt) / 1000
          : Date.now() / 1000;
        const utcS = Number.isFinite(rawUtcS) ? rawUtcS : Date.now() / 1000;
        const position = panoPosition(panorama);
        drawFrameCaption(full.getContext("2d"), full, { utcS, position });

        const yawDeg = Math.round((view.yaw * 180) / Math.PI);
        const pitchDeg = Math.round((view.pitch * 180) / Math.PI);
        const fullBlob = await encodeTaggedJpeg(full, {
          utcS, tz,
          lat: position?.lat ?? null, lon: position?.lon ?? null,
          description: `${meta.flightId} - panorama ${panorama.id}`,
          software: EXIF_SOFTWARE,
          userComment: `flight=${meta.flightId} panorama=${panorama.id} ` +
            `yaw=${yawDeg} pitch=${pitchDeg} video_t=${els.video.currentTime.toFixed(3)}`,
        });
        if (!fullBlob) {
          showError("Panorama freeze: could not encode the image.");
          return;
        }
        // Panorama ids come from operator-supplied panoramas.json — sanitise
        // before using one in a download filename.
        const safeId = String(panorama.id).replace(/[^a-zA-Z0-9_-]/g, "_");
        downloadBlob(fullBlob, `pano_${safeId}_${yawDeg}_${pitchDeg}.jpg`);

        if (typeof opts.onFrameCapture === "function") {
          const thumbBlob = await canvasToJpegBlob(buildThumbCanvas(full), 0.7);
          if (thumbBlob) {
            const frameId = uuid();
            await opts.onFrameCapture(frameId, fullBlob, thumbBlob,
              buildPanoFrameMetadata({
                panorama, view, videoCurrentTime: els.video.currentTime, utcS,
              }));
          } else {
            showError("Panorama freeze: could not encode the thumbnail.");
          }
        }
      }

      // Freeze button dispatcher: panorama capture when a panorama is open,
      // otherwise the (now zoom-aware) video capture.
      async function onFreezeClick() {
        if (panoHost !== null && panoHost.isActive()) {
          const cap = await panoHost.captureView();
          if (cap === null) {
            showError("Panorama freeze unavailable.");
            return;
          }
          await emitPanoFrame(cap);
          return;
        }
        return captureFrame();
      }

      container.querySelector(".vmc-btn-freeze").addEventListener("click", onFreezeClick);

      function uuid() {
        return globalThis.crypto?.randomUUID?.() ??
          "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
          });
      }

      const pois = new Map();

      // POI id → array of linked frame ids, most-recent first. Used to drive the
      // "Save frame" / "View saved frame" branch in cardHTML. Kept in sync with
      // the cloud-side frames list via handle.setFrames(...), and optimistically
      // updated on local save-frame clicks so the button flips state immediately.
      const framesByPoi = new Map();
      function ingestFrame(f) {
        if (!f || !f.poiId) return;
        const list = framesByPoi.get(f.poiId) ?? [];
        if (!list.includes(f.id)) list.unshift(f.id);
        framesByPoi.set(f.poiId, list);
      }
      for (const f of opts.initialFrames ?? []) ingestFrame(f);
      function poiFrameCtx(poi) {
        const list = framesByPoi.get(poi.id);
        if (list && list.length > 0) return { hasFrame: true, linkedFrameId: list[0] };
        return { hasFrame: false, linkedFrameId: null };
      }

      function poiIcon(L, color) {
        return L.divIcon({
          html: `<div class="vmc-poi-marker" style="background:${safeColor(color)}"></div>`,
          iconSize: [16, 16], iconAnchor: [8, 8], className: "",
        });
      }

      function captureThumb() {
        if (!canvasOK) return null;
        const TW = 320, TH = 180;
        const c = document.createElement("canvas");
        c.width = TW; c.height = TH;
        c.getContext("2d").drawImage(els.video, 0, 0, TW, TH);
        return c.toDataURL("image/jpeg", 0.7);
      }

      function renderPoi(poi, openPopup) {
        if (poi.marker) map.removeLayer(poi.marker);
        poi.marker = L.marker([poi.lat, poi.lon], {
          icon: poiIcon(L, poi.color), zIndexOffset: 900,
        }).addTo(map).bindTooltip(buildTooltipText(poi, { kind: "poi", fmtVid, reviewer: currentReviewer }), {
          permanent: true, direction: "top", offset: [0, -10], className: "vmc-pin-label",
        });

        const cardCtx = {
          kind: "poi",
          reviewer: currentReviewer,
          fmtVid,
          edit: !poi.saved,
          ...poiFrameCtx(poi),
        };
        poi.marker.bindPopup(buildCardHTML(poi, cardCtx), {
          minWidth: 320, maxWidth: 320,
          closeOnClick: false, autoClose: false,
          className: "vmc-card-popup",
        });

        poi.marker.on("popupopen", () => {
          // Leaflet rebuilds the card from the bound HTML on every open, and a
          // rename committed while the card was closing never reached that
          // HTML (the popup still counts as open then). Rebuild from the
          // item's current state first, then wire the fresh DOM.
          if (poi.saved) {
            poi.marker.setPopupContent(buildCardHTML(poi, {
              kind: "poi", reviewer: currentReviewer, fmtVid, edit: false, ...poiFrameCtx(poi),
            }));
          }
          attachCardHandlers(poi, "poi");
          // Focus + select the name input on first open of a brand-new POI so
          // the default name is replaced by the first keystroke. The blur
          // handler commits the value.
          if (!poi.saved) {
            poi._wasNew = true;
            const nameInput = poi.marker.getPopup().getElement()
              .querySelector(".vmc-card-name-input");
            if (nameInput) { nameInput.focus(); nameInput.select(); }
          }
        });

        // Click semantics are owned here, not by Leaflet: a single click
        // toggles the popup after a 250 ms pause; a double-click cancels the
        // pending toggle and seeks the video instead — no popup flash.
        // Leaflet's built-in click→toggle is detached (vendored 1.9.4, so the
        // private _openPopup reference is stable).
        poi.marker.off("click", poi.marker._openPopup, poi.marker);
        const clickIntent = createClickIntent({
          onSingle: () => {
            if (!poi.marker || !poi.marker._map) return; // marker was removed
            if (poi.marker.isPopupOpen()) poi.marker.closePopup();
            else poi.marker.openPopup();
          },
          onDouble: () => {
            if (typeof poi.t === "number") {
              els.video.currentTime = poi.t - gpsOffsetS;
            }
          },
        });
        poi.marker.on("click", () => clickIntent.click());
        // Still swallow the native dblclick so Leaflet's map zoom-on-dblclick
        // never fires (the teleport itself is driven by the click counter).
        poi.marker.on("dblclick", (e) => {
          L.DomEvent.stopPropagation(e); L.DomEvent.preventDefault(e);
        });

        if (openPopup) poi.marker.openPopup();
      }

      // POI-side funnel — same shape as localUpsertPin / localDeletePin.
      // Routes popup Save, Delete, and the cloud-side _setPois subscriber
      // through one canonical render path.
      function localUpsertPoi(serialized) {
        const existing = pois.get(serialized.id);
        if (existing && existing.marker) {
          // In-place update — keep the marker (and any open popup) alive so a
          // save or a background sync push never closes the card the reviewer
          // is editing. renderPoi() is for first paint only.
          const wasOpen = existing.marker.isPopupOpen();
          Object.assign(existing, serialized, { saved: true });
          existing.marker.setLatLng([existing.lat, existing.lon]);
          existing.marker.setIcon(poiIcon(L, existing.color));
          existing.marker.setTooltipContent(
            buildTooltipText(existing, { kind: "poi", fmtVid, reviewer: currentReviewer }),
          );
          if (!wasOpen) {
            // Refresh the bound content for the next open. Never touch an open
            // popup — the card handlers keep its DOM in sync in place.
            existing.marker.setPopupContent(
              buildCardHTML(existing, {
                kind: "poi", reviewer: currentReviewer, fmtVid,
                edit: false, ...poiFrameCtx(existing),
              }),
            );
          }
          return existing;
        }
        const fresh = { ...serialized, saved: true };
        pois.set(fresh.id, fresh);
        renderPoi(fresh, false);
        return fresh;
      }

      function localDeletePoi(id) {
        const existing = pois.get(id);
        if (existing && existing.marker) map.removeLayer(existing.marker);
        pois.delete(id);
      }

      function serializePoi(poi) {
        const now = new Date().toISOString();
        return {
          id: poi.id,
          schemaVersion: 1,
          t: poi.t, utc: poi.utc,
          lat: poi.lat, lon: poi.lon,
          name: poi.name, color: poi.color,
          notes: poi.notes ?? [],
          note: poi.note || undefined,
          thumb: poi.thumb || undefined,
          createdBy: ownerOnSerialize(poi),
          createdAt: poi.createdAt ?? now,
          modifiedAt: now,
        };
      }

      function serializePin(pin) {
        const now = new Date().toISOString();
        return {
          id: pin.id,
          schemaVersion: 1,
          lat: pin.lat, lon: pin.lon,
          name: pin.name, color: pin.color,
          notes: pin.notes ?? [],
          note: pin.note || undefined,
          createdBy: ownerOnSerialize(pin),
          createdAt: pin.createdAt ?? now,
          modifiedAt: now,
        };
      }

      function markPoi() {
        const t = els.video.currentTime;
        const gpsT = syncT(t);
        const pos = interpolate(telemetry, gpsT, headingStride);
        if (!pos) return;
        const utc = videoStartUtcSec + gpsT;
        const id = uuid();
        const poi = {
          id, t: gpsT, utc, lat: pos.lat, lon: pos.lon,
          name: `POI ${pois.size + 1}`, color: "#00ccff",
          thumb: captureThumb(), saved: false,
        };
        // Stamp ownership up-front (so the editor's "Save frame" is enabled
        // immediately — frameRowHTML gates on createdBy), then auto-save.
        const serialized = serializePoi(poi);
        Object.assign(poi, serialized);
        pois.set(id, poi);
        renderPoi(poi, true);
        opts.onPoiCreate?.(serialized);
      }
      container.querySelector(".vmc-btn-poi").addEventListener("click", markPoi);

      // Saved Frames button (conditional).
      const framesBtnEl = container.querySelector("#vmc-frames-btn");
      if (framesBtnEl) framesBtnEl.addEventListener("click", () => opts.onOpenFrames());

      handle._setPois = (newPois) => {
        const incoming = new Set(newPois.map((p) => p.id));
        for (const id of [...pois.keys()]) {
          if (!incoming.has(id)) localDeletePoi(id);
        }
        for (const data of newPois) localUpsertPoi(data);
      };

      if (Array.isArray(opts.initialPois)) {
        for (const data of opts.initialPois) localUpsertPoi(data);
      }

      // Open a panorama. Seeks the video to the panorama's moment in the
      // flight so the rAF sync loop places and orients the heli from
      // telemetry — identical for a marker click and a deep-link, and the
      // heli stays put when the panorama closes (the video time is unchanged).
      function openPanoramaAt(pano, view) {
        const snap = nearestOnTrack({ lat: pano.lat, lng: pano.lon });
        if (snap && typeof snap.t === "number") {
          els.video.currentTime = snap.t - gpsOffsetS;
        }
        panoHost.open(pano, {
          video: els.video,
          initialView: view,
          caption: pano.label,
        });
      }

      // Optional: render 360° panorama markers.
      const panoramas = Array.isArray(opts.initialPanoramas)
        ? opts.initialPanoramas
        : [];
      if (panoramas.length > 0) {
        const clipEl = container.querySelector(".vmc-video-clip");
        panoHost = createPanoHost(clipEl);
        cleanup.push(() => panoHost?.close?.());
        // Display labels. `nearestToLatLng` is a hoisted function declaration
        // reading only `telemetry`, so it is safe this early — `nearestOnTrack`
        // is not (it reads projection state declared further down).
        const panoOrds = panoOrdinals(panoramas);
        for (const pano of panoramas) {
          const near = nearestToLatLng({ lat: pano.lat, lng: pano.lon });
          pano.label = panoLabel(pano, panoOrds.get(pano.id), near ? near.t : null);
        }
        for (const pano of panoramas) {
          const icon = L.divIcon({
            className: "vmc-pano-icon",
            iconSize: [24, 24],
            html:
              '<svg viewBox="0 0 24 24" width="20" height="20" ' +
              'style="display:block;margin:2px auto;" aria-hidden="true">' +
              '<circle cx="12" cy="12" r="9" fill="none" stroke="#fff" ' +
              'stroke-width="2"/><path d="M3 12 H21 M12 3 V21" ' +
              'stroke="#fff" stroke-width="1.5" fill="none"/></svg>',
          });
          const marker = L.marker([pano.lat, pano.lon], {
            icon,
            zIndexOffset: 1100,
            title: pano.label,
          }).addTo(map);
          marker.bindTooltip(pano.label, { direction: "top" });
          marker.getElement()?.setAttribute("data-tour", "viewer.pano");
          marker.on("click", () => { openPanoramaAt(pano); });
          cleanup.push(() => map.removeLayer(marker));
        }
      }

      // Deep-link replay: open a panorama by id at a saved PSV view. No-op if
      // the id is unknown or there are no panoramas in this flight.
      handle._openPanorama = (panoramaId, view) => {
        const pano = panoramas.find((p) => p.id === panoramaId);
        if (pano && panoHost !== null) {
          openPanoramaAt(pano, view);
        } else {
          console.warn("[pano] openPanorama: unknown panorama id:", panoramaId);
        }
      };
      // Drain a deep-link that arrived before _openPanorama was patched.
      // Deferred to a microtask so the rest of this setup pass finishes first
      // — openPanoramaAt -> nearestOnTrack reads track-projection state
      // (segPxCache) that is declared further down.
      if (handle._pendingPanorama) {
        queueMicrotask(() => {
          const pend = handle._pendingPanorama;
          if (!pend || disposed) return;
          handle._pendingPanorama = null;
          handle._openPanorama(pend.panoramaId, pend.view);
        });
      }

      const pins = new Map();
      let pinMode = false;

      function pinIcon(L, color) {
        return L.divIcon({
          html: `<div class="vmc-pin-marker" style="background:${safeColor(color, "#ff4444")}"></div>`,
          iconSize: [14, 14], iconAnchor: [7, 7], className: "",
        });
      }

      function renderPin(pin, openPopup) {
        if (pin.marker) map.removeLayer(pin.marker);
        pin.marker = L.marker([pin.lat, pin.lon], {
          icon: pinIcon(L, pin.color), draggable: true,
        }).addTo(map).bindTooltip(buildTooltipText(pin, { kind: "pin", reviewer: currentReviewer }), {
          permanent: true, direction: "top", offset: [0, -10], className: "vmc-pin-label",
        });

        const cardCtx = {
          kind: "pin",
          reviewer: currentReviewer,
          fmtVid,
          edit: !pin.saved,
          hasFrame: false,
          linkedFrameId: null,
        };
        pin.marker.bindPopup(buildCardHTML(pin, cardCtx), {
          minWidth: 320, maxWidth: 320,
          closeOnClick: false, autoClose: false,
            className: "vmc-card-popup",
          },
        );

        pin.marker.on("popupopen", () => {
          // See renderPoi: rebuild a saved item's card from current state.
          if (pin.saved) {
            pin.marker.setPopupContent(buildCardHTML(pin, {
              kind: "pin", reviewer: currentReviewer, fmtVid, edit: false,
              hasFrame: false, linkedFrameId: null,
            }));
          }
          attachCardHandlers(pin, "pin");
          if (!pin.saved) {
            pin._wasNew = true;
            const nameInput = pin.marker.getPopup().getElement()
              .querySelector(".vmc-card-name-input");
            if (nameInput) { nameInput.focus(); nameInput.select(); }
          }
        });

        pin.marker.on("dragend", () => {
          if (!isOwn(pin)) return;
          const ll = pin.marker.getLatLng();
          pin.lat = ll.lat;
          pin.lon = ll.lng;
          const serialized = serializePin(pin);
          // localUpsertPin updates the existing marker in place (setLatLng) —
          // no remove/re-render, so the drag transition stays smooth.
          localUpsertPin(serialized);
          opts.onPinUpdate?.(serialized);
        });
        if (openPopup) pin.marker.openPopup();
      }

      // Single canonical render path. Every pin mutation — popup Save,
      // dragend, Delete, Delete-note, and the cloud-side
      // _setPins subscriber — funnels through these two helpers exactly
      // once. Prevents the "trailing renderPin paints an orphan marker"
      // duplication bug.
      function localUpsertPin(serialized) {
        const existing = pins.get(serialized.id);
        if (existing && existing.marker) {
          // In-place update — keep the marker and any open popup alive.
          const wasOpen = existing.marker.isPopupOpen();
          Object.assign(existing, serialized, { saved: true });
          existing.marker.setLatLng([existing.lat, existing.lon]);
          existing.marker.setIcon(pinIcon(L, existing.color));
          existing.marker.setTooltipContent(
            buildTooltipText(existing, { kind: "pin", reviewer: currentReviewer }),
          );
          if (!wasOpen) {
            existing.marker.setPopupContent(
              buildCardHTML(existing, {
                kind: "pin", reviewer: currentReviewer, fmtVid,
                edit: false, hasFrame: false, linkedFrameId: null,
              }),
            );
          }
          return existing;
        }
        const fresh = { ...serialized, saved: true };
        pins.set(fresh.id, fresh);
        renderPin(fresh, false);
        return fresh;
      }

      function localDeletePin(id) {
        const existing = pins.get(id);
        if (existing && existing.marker) map.removeLayer(existing.marker);
        pins.delete(id);
      }

      // Single delegated event wiring for pin and POI card popups.
      // Replaces the legacy per-button `addEventListener` loops, and is the
      // only place that mutates pin/POI state from popup interactions.
      function attachCardHandlers(item, kind) {
        const popup = item.marker.getPopup();
        const root = popup.getElement().querySelector(".vmc-card-body");
        if (!root || root.dataset.vmcWired === "1") return;
        root.dataset.vmcWired = "1";

        L.DomEvent.disableClickPropagation(root);
        L.DomEvent.disableScrollPropagation(root);

        const upsert = kind === "pin" ? localUpsertPin : localUpsertPoi;
        const serialize = kind === "pin" ? serializePin : serializePoi;
        const onCreate = kind === "pin" ? opts.onPinCreate : opts.onPoiCreate;
        const onUpdate = kind === "pin" ? opts.onPinUpdate : opts.onPoiUpdate;
        const onDelete = kind === "pin" ? opts.onPinDelete : opts.onPoiDelete;
        const localDelete = kind === "pin" ? localDeletePin : localDeletePoi;

        const saveItem = (markSaved) => {
          if (markSaved) item.saved = true;
          const s = serialize(item);
          upsert(s);
          if (item._wasNew) {
            item._wasNew = false;
            onCreate?.(s);
          } else {
            onUpdate?.(s);
          }
          return s;
        };

        // Commit a pending rename from the name field. Runs on blur and on
        // every way the card can close (×, Escape, a click on the map), so a
        // name typed and never blurred is not thrown away with the popup.
        // Only a field the reviewer typed in commits: a re-opened card is
        // rebuilt from the bound HTML and can show a stale name, and a
        // deleted item must never be resurrected by its own closing popup.
        let nameDirty = false;
        root.addEventListener("input", (ev) => {
          if (ev.target.classList?.contains("vmc-card-name-input")) nameDirty = true;
        }, true);
        const commitName = () => {
          const input = root.querySelector(".vmc-card-name-input");
          if (!input) return;
          const next = shouldCommitName({
            dirty: nameDirty, deleted: item._deleted === true, own: isOwn(item),
            inputValue: input.value, currentName: item.name,
          });
          if (next === null) return;
          nameDirty = false;
          item.name = next;
          const nameEl = root.querySelector(".vmc-card-name");
          if (nameEl) nameEl.textContent = next;
          saveItem(true);
        };
        // The card DOM is rebuilt on every open, so these handlers are wired
        // per open; the marker-level close listener is wired once per item
        // and always calls the closure for the DOM currently open.
        item._commitName = commitName;
        if (!item._closeWired) {
          item._closeWired = true;
          item.marker.on("popupclose", () => item._commitName?.());
        }

        root.addEventListener("click", (ev) => {
          const btn = ev.target.closest("[data-act]");
          if (!btn || btn.hasAttribute("disabled")) return;
          const act = btn.getAttribute("data-act");

          if (act === "close") { commitName(); item.marker.closePopup(); return; }

          if (act === "jump") {
            if (kind === "poi" && typeof item.t === "number") {
              els.video.currentTime = item.t - gpsOffsetS;
            } else if (kind === "pin") {
              const near = nearestToLatLng({ lat: item.lat, lng: item.lon });
              if (near && typeof near.t === "number") {
                els.video.currentTime = near.t - gpsOffsetS;
              }
            }
            return;
          }

          if (act === "edit") {
            if (!isOwn(item)) return;
            root.classList.add("vmc-editing");
            root.querySelector(".vmc-card-name-input")?.focus();
            return;
          }

          if (act === "done") {
            // Force blur of any in-edit fields so the blur handler persists.
            if (document.activeElement && root.contains(document.activeElement)) {
              document.activeElement.blur();
            }
            root.classList.remove("vmc-editing");
            return;
          }

          if (act === "swatch") {
            if (!isOwn(item)) return;
            const color = btn.getAttribute("data-color");
            item.color = color;
            // Repaint swatch selection state.
            root.querySelectorAll(".vmc-swatch").forEach((el) => {
              el.classList.toggle("vmc-swatch-selected", el.getAttribute("data-color")?.toLowerCase() === color.toLowerCase());
            });
            // Repaint the title-row swatch too.
            const titleSwatch = root.querySelector(".vmc-card-swatch");
            if (titleSwatch) titleSwatch.style.background = color;
            saveItem(true);
            return;
          }

          if (act === "swatch-custom") {
            if (!isOwn(item)) return;
            const hidden = root.querySelector(".vmc-card-color-hidden");
            if (hidden) hidden.click();
            return;
          }

          if (act === "delete-ask") {
            root.querySelector(".vmc-card-delete-confirm")?.classList.remove("hidden");
            return;
          }
          if (act === "delete-no") {
            root.querySelector(".vmc-card-delete-confirm")?.classList.add("hidden");
            return;
          }
          if (act === "delete-yes") {
            item._deleted = true;
            localDelete(item.id);
            onDelete?.(item.id);
            return;
          }

          // ─── Chat-style notes ───────────────────────────────────────
          // Each note is its own message keyed by `id`. Anyone can post,
          // owner-only edit, owner+admin+pin-creator can delete.

          if (act === "note-post") {
            if (!currentReviewer) return;
            const ta = root.querySelector(".vmc-card-notes-compose .vmc-card-note-input");
            if (!ta) return;
            const text = (ta.value || "").trim();
            if (!text) return;
            const baseNotes = migrateLegacyNote(item);
            item.notes = appendNote(baseNotes, currentReviewer, text, () => new Date().toISOString());
            delete item.note;
            saveItem(true);
            ta.value = "";
            const listEl = root.querySelector(".vmc-card-notes-list");
            if (listEl) listEl.innerHTML = buildNotesListHTML(item, currentReviewer);
            return;
          }

          if (act === "note-edit") {
            const id = btn.getAttribute("data-note-id");
            const baseNotes = migrateLegacyNote(item);
            const n = baseNotes.find((x) => x.id === id);
            if (!n) return;
            if (!currentReviewer || n.reviewerId !== currentReviewer.reviewerId) return;
            const row = btn.closest(".vmc-card-note-row");
            if (!row) return;
            row.outerHTML = buildNoteRowHTML(n, {
              reviewer: currentReviewer,
              canDeleteOthers: canDeleteOthersNotes(item, currentReviewer),
              mode: "edit",
            });
            const fresh = root.querySelector(`.vmc-card-note-row[data-note-id="${id}"] .vmc-card-note-edit`);
            fresh?.focus();
            return;
          }

          if (act === "note-edit-cancel") {
            const id = btn.getAttribute("data-note-id");
            const baseNotes = migrateLegacyNote(item);
            const n = baseNotes.find((x) => x.id === id);
            if (!n) return;
            const row = btn.closest(".vmc-card-note-row");
            if (!row) return;
            row.outerHTML = buildNoteRowHTML(n, {
              reviewer: currentReviewer,
              canDeleteOthers: canDeleteOthersNotes(item, currentReviewer),
              mode: "read",
            });
            return;
          }

          if (act === "note-edit-save") {
            const id = btn.getAttribute("data-note-id");
            const baseNotes = migrateLegacyNote(item);
            const n = baseNotes.find((x) => x.id === id);
            if (!n) return;
            if (!currentReviewer || n.reviewerId !== currentReviewer.reviewerId) return;
            const row = btn.closest(".vmc-card-note-row");
            if (!row) return;
            const ta = row.querySelector(".vmc-card-note-edit");
            if (!ta) return;
            const text = (ta.value || "").trim();
            if (text && text !== n.text) {
              item.notes = editNoteById(baseNotes, id, text, () => new Date().toISOString());
              saveItem(true);
            }
            const updated = (item.notes || baseNotes).find((x) => x.id === id) || n;
            row.outerHTML = buildNoteRowHTML(updated, {
              reviewer: currentReviewer,
              canDeleteOthers: canDeleteOthersNotes(item, currentReviewer),
              mode: "read",
            });
            return;
          }

          if (act === "note-delete") {
            const id = btn.getAttribute("data-note-id");
            const baseNotes = migrateLegacyNote(item);
            const n = baseNotes.find((x) => x.id === id);
            if (!n) return;
            const own = currentReviewer && n.reviewerId === currentReviewer.reviewerId;
            if (!own && !canDeleteOthersNotes(item, currentReviewer)) return;
            item.notes = removeNoteById(baseNotes, id);
            delete item.note;
            saveItem(true);
            btn.closest(".vmc-card-note-row")?.remove();
            return;
          }

          if (act === "save-frame") {
            if (kind !== "poi" || typeof item.t !== "number") return;
            // The first press loads the hidden video, which takes a moment.
            // Once the upload lands, setFrames flips the card to "View saved
            // frame"; until then the button says it is busy.
            const label = btn.textContent;
            btn.disabled = true;
            btn.textContent = "Saving frame…";
            capturePoiFrame(item).finally(() => {
              if (btn.isConnected) { btn.disabled = false; btn.textContent = label; }
            });
            return;
          }

          if (act === "view-frame") {
            if (kind !== "poi") return;
            const fid = btn.getAttribute("data-frame-id");
            if (fid) opts.onViewFrame?.(fid);
            return;
          }
        });

        // The native colour picker fires "input" + "change" events. We treat
        // "change" as the commit signal (mirrors swatch behaviour).
        const hidden = root.querySelector(".vmc-card-color-hidden");
        if (hidden) {
          hidden.addEventListener("change", () => {
            if (!isOwn(item)) return;
            item.color = hidden.value;
            const titleSwatch = root.querySelector(".vmc-card-swatch");
            if (titleSwatch) titleSwatch.style.background = item.color;
            root.querySelectorAll(".vmc-swatch").forEach((el) => el.classList.remove("vmc-swatch-selected"));
            saveItem(true);
          });
        }

        // Capture-phase blur: blur doesn't bubble by default. Used for the
        // name input (note edits commit explicitly via Save buttons).
        root.addEventListener("blur", (ev) => {
          const t = ev.target;

          if (t.classList?.contains("vmc-card-name-input")) {
            commitName();
            return;
          }
        }, true);
      }

      function togglePinMode() {
        pinMode = !pinMode;
        map.getContainer().style.cursor = pinMode ? "crosshair" : "";
      }
      // Cards are bound with autoClose: false, so several can be open at
      // once; closing "the" popup would leave the older ones up.
      const closeAllCards = () => {
        for (const it of [...pois.values(), ...pins.values()]) {
          if (it.marker?.isPopupOpen()) it.marker.closePopup();
        }
      };
      map.on("click", (e) => {
        if (!pinMode) {
          // Click away: a click on the map closes the open cards. A pending
          // rename commits on popupclose.
          closeAllCards();
          return;
        }
        const id = uuid();
        const pin = { id, lat: e.latlng.lat, lon: e.latlng.lng,
                      name: `Pin ${pins.size + 1}`, color: "#ff4444", saved: false };
        // Stamp ownership up-front, then auto-save on creation.
        const serialized = serializePin(pin);
        Object.assign(pin, serialized);
        pins.set(id, pin);
        renderPin(pin, true);
        opts.onPinCreate?.(serialized);
        togglePinMode();
      });
      const onEsc = (e) => {
        if (e.key !== "Escape") return;
        if (pinMode) togglePinMode();
        closeAllCards();
      };
      document.addEventListener("keydown", onEsc);
      cleanup.push(() => document.removeEventListener("keydown", onEsc));

      container.querySelector(".vmc-btn-pin").addEventListener("click", togglePinMode);

      if (Array.isArray(opts.initialPins)) {
        for (const data of opts.initialPins) localUpsertPin(data);
      }

      handle._setPins = (newPins) => {
        const incoming = new Set(newPins.map((p) => p.id));
        for (const id of [...pins.keys()]) {
          if (!incoming.has(id)) localDeletePin(id);
        }
        for (const data of newPins) localUpsertPin(data);
      };

      // _setFrames subscriber — keeps framesByPoi in sync with the cloud
      // app's frames state. Rebuilds from scratch so deletions are reflected.
      // Also refreshes the currently-open POI card's frame row in place,
      // so the active reviewer's textarea is never wiped by a re-render.
      handle._setFrames = (frames) => {
        framesByPoi.clear();
        for (const f of frames ?? []) ingestFrame(f);
        const el = container.querySelector("#vmc-frames-count");
        if (el) el.textContent = String((frames ?? []).length);
        const openPopup = document.querySelector(".vmc-card-popup .vmc-card-body");
        if (!openPopup || openPopup.getAttribute("data-kind") !== "poi") return;
        for (const p of pois.values()) {
          if (p.marker?.getPopup()?.getElement()?.contains(openPopup)) {
            const newCtx = poiFrameCtx(p);
            const row = openPopup.querySelector(".vmc-card-frame-row");
            if (!row) return;
            if (newCtx.hasFrame && newCtx.linkedFrameId) {
              row.innerHTML = `<button type="button" data-act="view-frame" data-frame-id="${newCtx.linkedFrameId}" class="vmc-card-link">View saved frame</button>`;
            } else {
              const disabled = !p.createdBy ? " disabled" : "";
              row.innerHTML = `<button type="button" data-act="save-frame" class="vmc-card-link"${disabled}>Save frame</button>`;
            }
            return;
          }
        }
      };

      let dragging = false;
      let lastT = -1;
      let rafHandle = 0;
      function syncFrame() {
        if (dragging) {
          rafHandle = requestAnimationFrame(syncFrame);
          return;
        }
        const t = els.video.currentTime;
        if (t !== lastT) {
          lastT = t;
          const gpsT = syncT(t);
          const pos = interpolate(telemetry, gpsT, headingStride);
          if (pos) {
            heliMarker.setLatLng([pos.lat, pos.lon]);
            lastHeliPos = {
              lat: pos.lat, lon: pos.lon,
              heading: pos.heading ?? lastHeliPos?.heading ?? null,
            };
            // Recentre/rotate first so paintHeliHeading reads the tick's
            // final bearing (a no-op while follow is off).
            followCtl.update(lastHeliPos);
            paintHeliHeading();
            const utc = videoStartUtcSec + gpsT;
            ib.vidTime.textContent = fmtVid(t);
            ib.localTime.textContent = fmtClock(utc, tz);
            ib.utcTime.textContent = fmtUTC(utc);
            ib.lat.textContent = pos.lat.toFixed(6);
            ib.lon.textContent = pos.lon.toFixed(6);
            // Altitude + heading are read from the track.json sample at
            // the bracketing index — track.json carries rel_alt/abs_alt/
            // yaw/pitch from the FMV telemetry. Falls back to '—' for
            // GPX-only flights where these fields are absent.
            const sample = telemetry[pos.i];
            const alt = pos.abs_alt ?? sample?.abs_alt ?? sample?.rel_alt;
            ib.alt.textContent = alt != null ? (alt * M_TO_FT).toFixed(0) + "ft" : "—";
            ib.hdg.textContent = pos.heading != null
              ? ((pos.heading + 360) % 360).toFixed(0) + "°"
              : "—";
            ib.gpsDelta.textContent = Math.abs(gpsT - sample.t).toFixed(1) + "s";
          }
        }
        rafHandle = requestAnimationFrame(syncFrame);
      }
      rafHandle = requestAnimationFrame(syncFrame);
      cleanup.push(() => cancelAnimationFrame(rafHandle));

      function nearestToLatLng(ll) {
        let best = telemetry[0], bestD = Infinity;
        for (const p of telemetry) {
          const d = (p.lat - ll.lat) ** 2 + (p.lon - ll.lng) ** 2;
          if (d < bestD) { bestD = d; best = p; }
        }
        return best;
      }

      // Project a point onto the nearest polyline segment in screen-space
      // (so visual snap matches what the user sees regardless of latitude).
      // Returns { lat, lon, t, heading } where t is interpolated between
      // the two bracketing telemetry samples by the foot's fractional
      // position along the segment.
      //
      // `aroundT` (optional, seconds): when set, candidate segments are
      // soft-biased away from times far from aroundT. This stops the
      // marker jumping between forward and back branches of a
      // crossover/loop during a drag — the previous-tick t keeps it on
      // one branch. See viewer.geometry.js for the bias model.
      //
      // TODO(24Hz): O(n) per drag tick. At 1Hz / ≤2h flights this is
      // ≤7200 iterations × ~60Hz drag events — comfortable. When SRT
      // cadence bumps to 24Hz (see /memories/repo/future-24hz-srt.md)
      // a multi-hour flight pushes ~350k iter/event; replace with a
      // coarse spatial index (segment bbox grid or rdp simplification).
      let segPxCache = null;
      let segPxCacheZoom = -1;
      function projectedSegPts() {
        const z = map.getZoom();
        if (segPxCache && segPxCacheZoom === z) return segPxCache;
        segPxCache = telemetry.map((p) => map.latLngToLayerPoint([p.lat, p.lon]));
        segPxCacheZoom = z;
        return segPxCache;
      }
      map.on("zoomend", () => { segPxCache = null; });

      function nearestOnTrack(ll, aroundT) {
        if (telemetry.length < 2) return nearestToLatLng(ll);
        const target = map.latLngToLayerPoint(ll);
        const r = pickNearestOnPath(
          target, projectedSegPts(), telemetry, aroundT, DRAG_BIAS_PX_PER_SEC,
        );
        if (!r) return null;
        const footLL = map.layerPointToLatLng([r.fx, r.fy]);
        // Bearing over a macro segment (see interpolate()) so the heading
        // returned to click-to-seek + drag callers is stable on 24 Hz
        // telemetry instead of jittering with GPS noise.
        const { aIdx, bIdx } = macroSegmentEndpoints(
          r.segIndex, headingStride, telemetry.length,
        );
        const a = telemetry[aIdx], b = telemetry[bIdx];
        return { lat: footLL.lat, lon: footLL.lng, t: r.t, heading: bearing(a, b) };
      }

      // Click polyline → seek.
      mapHandles.trackLine.on("click", (e) => {
        const p = nearestOnTrack(e.latlng);
        if (p && typeof p.t === "number") {
          els.video.currentTime = p.t - gpsOffsetS;
        }
        L.DomEvent.stopPropagation(e);
      });

      // Drag heli → throttled seek. Each tick advances dragLastT by
      // the *signed* projection of the cursor delta onto the local
      // path tangent, in seconds:
      //
      //   dT = (cursor delta · segment vector) * segDur / segLen²
      //
      // Forward (+) when the cursor moves with the path, backward (−)
      // when against it, exactly zero when truly perpendicular. No
      // cumulative state, no history-derived sign — direction at every
      // tick reflects the cursor's *current* relationship to the local
      // path. The user has to drag roughly along the line to scrub,
      // which is exactly the "follow the line" gesture.
      let dragSeekTimer = null;
      let pendingDragT = null;
      let dragLastT = null;
      let dragLastCursor = null;
      function flushDragSeek() {
        if (pendingDragT !== null) {
          els.video.currentTime = pendingDragT - gpsOffsetS;
          pendingDragT = null;
        }
        dragSeekTimer = null;
      }
      function segmentIndexAt(t) {
        if (telemetry.length < 2) return -1;
        if (t <= telemetry[0].t) return 0;
        if (t >= telemetry[telemetry.length - 1].t) return telemetry.length - 2;
        let lo = 0, hi = telemetry.length - 1;
        while (hi - lo > 1) {
          const m = (lo + hi) >> 1;
          if (telemetry[m].t <= t) lo = m; else hi = m;
        }
        return lo;
      }
      // TEMP DIAGNOSTIC: enable with `?dragdebug=1` in the URL.
      const DRAG_DEBUG = new URLSearchParams(location.search).has("dragdebug");
      let dragTick = 0;
      heliMarker.on("dragstart", (e) => {
        dragging = true;
        els.video.pause();
        dragLastT = syncT(els.video.currentTime);
        dragLastCursor = map.latLngToLayerPoint(e.target.getLatLng());
        dragTick = 0;
        if (DRAG_DEBUG) {
          console.log("[drag] START vt=%s gpsT=%s zoom=%s",
            els.video.currentTime.toFixed(3), dragLastT.toFixed(3),
            map.getZoom());
        }
      });
      heliMarker.on("drag", (e) => {
        const cursor = map.latLngToLayerPoint(e.target.getLatLng());
        const dx = cursor.x - dragLastCursor.x;
        const dy = cursor.y - dragLastCursor.y;
        dragLastCursor = cursor;

        let dT = 0;
        let segLen2 = 0, localProj = 0, segDur = 0;
        let segIdx = segmentIndexAt(dragLastT);
        const segPxs = (segIdx >= 0) ? projectedSegPts() : null;
        // Some telemetry sources (notably duplicate-timestamp GPX/FMV
        // samples) produce *degenerate* segments where segLen2==0 and/or
        // segDur==0. If we sit on one, projection yields dT=0 forever
        // and the heli gets stuck. Hop to the nearest non-degenerate
        // segment instead (forward by default; backward as fallback).
        const isValidSeg = (i) => {
          if (!segPxs || i < 0 || i + 1 >= telemetry.length) return false;
          const a = segPxs[i], b = segPxs[i + 1];
          const sdx = b.x - a.x, sdy = b.y - a.y;
          if (sdx * sdx + sdy * sdy === 0) return false;
          if (telemetry[i + 1].t - telemetry[i].t === 0) return false;
          return true;
        };
        if (segPxs && segIdx >= 0 && !isValidSeg(segIdx)) {
          let fwd = segIdx + 1;
          while (fwd + 1 < telemetry.length && !isValidSeg(fwd)) fwd++;
          let bwd = segIdx - 1;
          while (bwd >= 0 && !isValidSeg(bwd)) bwd--;
          const fwdOK = isValidSeg(fwd);
          const bwdOK = isValidSeg(bwd);
          if (fwdOK && (!bwdOK || (fwd - segIdx) <= (segIdx - bwd))) {
            segIdx = fwd;
            dragLastT = telemetry[segIdx].t;
          } else if (bwdOK) {
            segIdx = bwd;
            dragLastT = telemetry[segIdx + 1].t;
          }
        }
        if (segPxs && segIdx >= 0 && segIdx + 1 < telemetry.length) {
          // At high cadence (24 Hz) adjacent samples are sub-pixel apart, so
          // segLen² rounds to ~zero and dT collapses — drag becomes a no-op.
          // Project over a `headingStride`-wide macro segment (the same
          // stride as the bearing computation) so the baseline is ~1 s of
          // telemetry in screen pixels. At 1 Hz (stride 1) these endpoints
          // are exactly (segIdx, segIdx + 1), so the degenerate-segment hop
          // above and this projection agree — legacy behaviour preserved.
          const { aIdx, bIdx } = macroSegmentEndpoints(
            segIdx, headingStride, telemetry.length,
          );
          const aPx = segPxs[aIdx], bPx = segPxs[bIdx];
          const segDx = bPx.x - aPx.x, segDy = bPx.y - aPx.y;
          segLen2 = segDx * segDx + segDy * segDy;
          if (segLen2 > 0) {
            localProj = dx * segDx + dy * segDy;                // signed
            segDur = telemetry[bIdx].t - telemetry[aIdx].t;
            dT = localProj * segDur / segLen2;                  // signed seconds
          }
        }

        const minT = telemetry[0].t;
        const maxT = telemetry[telemetry.length - 1].t;
        const prevDragLastT = dragLastT;
        dragLastT = Math.max(minT, Math.min(maxT, dragLastT + dT));

        const pos = interpolate(telemetry, dragLastT, headingStride);
        if (pos) {
          heliMarker.setLatLng([pos.lat, pos.lon]);
          lastHeliPos = {
            lat: pos.lat, lon: pos.lon,
            heading: pos.heading ?? lastHeliPos?.heading ?? null,
          };
          // No followCtl.update here — the map must stay put under the
          // cursor while the heli is drag-scrubbed; follow resumes on the
          // first sync tick after dragend.
          paintHeliHeading();
        }
        pendingDragT = dragLastT;
        if (!dragSeekTimer) dragSeekTimer = setTimeout(flushDragSeek, 140);

        if (DRAG_DEBUG) {
          dragTick++;
          console.log(
            "[drag] #%d cursor=(%s,%s) d=(%s,%s) segIdx=%d segLen2=%s " +
            "segDur=%s proj=%s dT=%s gpsT=%s→%s vt=%s seeking=%s",
            dragTick,
            cursor.x.toFixed(1), cursor.y.toFixed(1),
            dx.toFixed(1), dy.toFixed(1),
            segIdx, segLen2.toFixed(1),
            segDur.toFixed(3), localProj.toFixed(1),
            dT.toFixed(3),
            prevDragLastT.toFixed(3), dragLastT.toFixed(3),
            els.video.currentTime.toFixed(3),
            els.video.seeking,
          );
        }
      });
      heliMarker.on("dragend", () => {
        dragging = false;
        clearTimeout(dragSeekTimer); dragSeekTimer = null;
        if (dragLastT !== null) {
          els.video.currentTime = dragLastT - gpsOffsetS;
        }
        dragLastT = null;
        dragLastCursor = null;
      });
    } catch (e) {
      showError(e.message);
    }
  })();

  return handle;
}
